<template>
  <AppShell>
    <section id="v-maya" class="view on maya-vue" :class="{ 'wb-on': workbenchOpen, 'wb-card-on': workbenchOpen && !workbenchWide }" aria-labelledby="maya-title">
      <div class="mayawrap">
        <div class="pagehead">
          <div class="mhead"><img class="mheadav" :src="mayaImage" alt="Maya"><div><h1 id="maya-title">Maya <span class="mtag">{{ t("maya_role") }}</span></h1><p class="sub">{{ t(rbarLatest ? "maya_sub" : "maya_sub0") }}</p></div></div>
        </div>

        <!-- GEO_EXEC_R18 · the one thing to do next, ahead of the conversation, not inside it. -->
        <BrandNextAction v-if="!loading && !error" :brand-id="currentBrand?.id" variant="card" />

        <p v-if="loading" role="status" class="empty">{{ t("maya_opening") }}</p>
        <p v-else-if="error" role="alert" class="empty">{{ error }} <button type="button" class="atbtn" @click="load">{{ t("account_retry") }}</button></p>
        <div v-else-if="showIntro" id="maya_intro" class="maya-intro">
          <img class="mintroav" :src="mayaLargeImage" alt="Maya">
          <h2>{{ t("maya_meet") }}</h2>
          <p class="mintrobio">{{ t("maya_bio0") }}</p>
          <button type="button" class="btn" @click="meetMaya">{{ t("maya_sayhi") }}</button>
        </div>
        <div v-else-if="noReport" id="maya_noreport" class="maya-noreport">
          <div class="empty">{{ t("maya_noreport") }}</div>
          <button type="button" class="btn" @click="startAddBrandConversation">{{ t("maya_noreport_cta") }}</button>
        </div>

        <div v-else id="maya_chat">
          <div v-if="!messages.length && !articleStage" class="maya-hint">{{ t(rbarLatest ? "maya_hint" : "maya_hint0") }}</div>
          <div v-if="showReportBar" id="maya_rbar" class="rbar" :class="{ 'rbar-cta': !rbarLatest }">
            <template v-if="rbarLatest">
              <span class="rb-s" :title="t('geo_hint')">{{ Math.round(Number(rbarLatest.score) || 0) }}</span><span class="rb-u">/100</span>
              <span class="rb-b" :title="t('band_tip')">{{ bandName(rbarLatest.band) }}</span><span class="rb-m">{{ rbarBrand.brand }}</span>
              <span class="rb-d" :title="t('rec_rate_tip')">{{ t("rec_rate") }} {{ Math.round(Number(rbarLatest.rec_pct) || 0) }}%</span>
              <button type="button" class="rb-open" @click="openCurrentReport">{{ t("rbar_open") }}</button>
            </template>
            <template v-else>
              <span class="rb-t">{{ t("rbar_none") }}</span>
              <button type="button" class="rb-go" @click="runCurrentBrandDiagnosis">{{ t("rbar_run") }}</button>
            </template>
          </div>
          <ol ref="threadEl" class="maya-thread maya-messages" aria-live="polite" @scroll="syncLatestButton">
            <li v-for="(message, index) in messages" :key="message.id || index" class="mmsg" :class="message.role === 'user' ? 'user' : 'maya'">
              <div class="mav" aria-hidden="true"><img v-if="message.role !== 'user'" class="mavimg" :src="mayaImage" alt=""><span v-else>{{ initials }}</span></div>
              <div class="mcol">
                <!-- pre-scan calibration cards (ported mc-onb family) -->
                <div v-if="message.special === 'brandask'" class="mc-cardwrap">
                  <div class="mc-onb mc-brandask mc-enter">
                    <div class="mc-onb-spine"></div>
                    <div class="mc-onb-in">
                      <div class="mc-onb-lab">{{ S.askBrand }}</div>
                      <div class="mc-onb-f"><input v-model="brandAskVal" class="mc-onb-field" type="text" autocomplete="off" :placeholder="S.askBrandPh" @keydown.enter.prevent="brandAskSubmit"></div>
                      <div class="mc-onb-actions">
                        <button class="mc-onb-go" type="button" @click="brandAskSubmit">{{ S.start }}</button>
                        <button class="mc-onb-cancel" type="button" @click="cancelAddBrandConversation">{{ S.cancelAdd }}</button>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else-if="message.special === 'region' && intake" class="mc-cardwrap">
                  <div class="mc-onb mc-cfm mc-enter">
                    <div class="mc-onb-spine"></div>
                    <div class="mc-onb-in">
                      <div class="mc-cfm-row"><span class="mc-cfm-l">{{ S.country }}</span>
                        <select v-model="intake.ui.country" class="mc-onb-field">
                          <option v-for="c in Q3_COUNTRIES" :key="c" :value="c">{{ c }}</option>
                          <option value="__other__">{{ S.other }}</option>
                        </select>
                      </div>
                      <input v-if="intake.ui.country === '__other__'" v-model="intake.ui.other" class="mc-onb-field" type="text" autocomplete="off" :placeholder="S.otherPh" style="margin-top:7px" @keydown.enter.prevent="regionGo">
                      <div v-if="regionCityRow" class="mc-cfm-row"><span class="mc-cfm-l">{{ S.city }}</span><input v-model="intake.ui.city" class="mc-onb-field" type="text" autocomplete="off" :placeholder="S.cityPh" @keydown.enter.prevent="regionGo"></div>
                      <button class="mc-onb-go mc-onb-gofull" type="button" @click="regionGo">{{ S.looksRightGo }}</button>
                    </div>
                  </div>
                </div>
                <div v-else-if="message.special === 'confirm' && intake" class="mc-cardwrap">
                  <div class="mc-onb mc-cfm mc-enter">
                    <div class="mc-onb-spine"></div>
                    <div class="mc-onb-in">
                      <div class="mc-cfm-hd">{{ S.got }}</div>
                      <div class="mc-cfm-row mc-cfm-brandrow"><span class="mc-cfm-l">{{ S.rowBrand }}</span>
                        <span class="mc-cfm-v">
                          <template v-if="confirmEdit.brand"><input v-model="confirmVals.brand" class="mc-cfm-brand mc-onb-field" type="text" autocomplete="off" @keydown.enter.prevent="confirmGo"></template>
                          <template v-else><span class="mc-cfm-brandtxt">{{ intake.brand }}</span> <button type="button" class="mc-cfm-edit" :title="S.edit" @click="editConfirm('brand')">{{ S.edit }}</button></template>
                        </span>
                      </div>
                      <div class="mc-cfm-row mc-cfm-site"><span class="mc-cfm-l">{{ S.rowSite }}</span>
                        <span v-if="intake.confSrc === 'given' || intake.confSrc === 'found'" class="mc-cfm-v mc-cfm-sitev">
                          <template v-if="confirmEdit.site"><input v-model="confirmVals.site" class="mc-cfm-url mc-onb-field" type="text" autocomplete="off" placeholder="https://" @keydown.enter.prevent="confirmGo"></template>
                          <template v-else><a :href="intake.confUrl" target="_blank" rel="noopener">{{ (intake.confUrl || "").replace(/^https?:\/\//, "") }}</a> <em class="mc-cfm-src">{{ intake.confSrc === "given" ? S.srcGiven : S.srcFound }}</em> <button type="button" class="mc-cfm-editsite" :title="S.edit" @click="editConfirm('site')">{{ S.edit }}</button></template>
                        </span>
                        <span v-else class="mc-cfm-v">
                          <input v-model="confirmVals.site" class="mc-cfm-url mc-onb-field" type="text" autocomplete="off" :placeholder="'https://… ' + S.onbOpt" @keydown.enter.prevent="confirmGo">
                          <span class="mc-cfm-note">{{ intake.confSrc === "found_low" ? S.siteNoteLow : S.siteNoteNone }}</span>
                        </span>
                      </div>
                      <div class="mc-cfm-row mc-cfm-sellrow"><span class="mc-cfm-l">{{ S.rowSell }}</span>
                        <span class="mc-cfm-v">
                          <template v-if="confirmEdit.sell"><input v-model="confirmVals.sell" class="mc-onb-field" type="text" autocomplete="off" @keydown.enter.prevent="confirmGo"></template>
                          <template v-else><span class="mc-cfm-selltxt">{{ intake.answers.sell || "" }}</span><em v-if="!intake.answers.sell" class="mc-cfm-src">{{ S.empty }}</em> <button type="button" class="mc-cfm-editsell" :title="S.edit" @click="editConfirm('sell')">{{ S.edit }}</button></template>
                        </span>
                      </div>
                      <div class="mc-cfm-row mc-cfm-buyerrow"><span class="mc-cfm-l">{{ S.rowBuyer }}</span>
                        <span class="mc-cfm-v">
                          <template v-if="confirmEdit.buyer"><input v-model="confirmVals.buyer" class="mc-onb-field" type="text" autocomplete="off" @keydown.enter.prevent="confirmGo"></template>
                          <template v-else><span class="mc-cfm-buyertxt">{{ intake.answers.buyer || "" }}</span><em v-if="!intake.answers.buyer" class="mc-cfm-src">{{ S.auto }}</em> <button type="button" class="mc-cfm-editbuyer" :title="S.edit" @click="editConfirm('buyer')">{{ S.edit }}</button></template>
                        </span>
                      </div>
                      <div class="mc-cfm-row mc-cfm-regionrow"><span class="mc-cfm-l">{{ S.rowRegion }}</span>
                        <span class="mc-cfm-v">
                          <template v-if="confirmEdit.region"><input v-model="confirmVals.region" class="mc-onb-field" type="text" autocomplete="off" placeholder="City, Country" @keydown.enter.prevent="confirmGo"></template>
                          <template v-else><span class="mc-cfm-regiontxt">{{ regionText(intake) }}</span><em v-if="!regionText(intake)" class="mc-cfm-src">{{ S.empty }}</em> <button type="button" class="mc-cfm-editregion" :title="S.edit" @click="editConfirm('region')">{{ S.edit }}</button></template>
                        </span>
                      </div>
                      <div v-if="intake.cat" class="mc-cfm-row mc-cfm-catrow"><span class="mc-cfm-l">{{ S.rowCat }}</span>
                        <span class="mc-cfm-v">
                          <template v-if="confirmEdit.cat"><input v-model="confirmVals.cat" class="mc-onb-field" type="text" autocomplete="off" @keydown.enter.prevent="confirmGo"></template>
                          <template v-else><span class="mc-cfm-cattxt">{{ intake.cat }}</span> <button type="button" class="mc-cfm-editcat" :title="S.edit" @click="editConfirm('cat')">{{ S.edit }}</button></template>
                        </span>
                      </div>
                      <div v-if="intake.comp" class="mc-cfm-row mc-cfm-comprow"><span class="mc-cfm-l">{{ S.rowComp }}</span>
                        <span class="mc-cfm-v">
                          <template v-if="confirmEdit.comp"><input v-model="confirmVals.comp" class="mc-onb-field" type="text" autocomplete="off" @keydown.enter.prevent="confirmGo"></template>
                          <template v-else><span class="mc-cfm-comptxt">{{ intake.comp }}</span> <button type="button" class="mc-cfm-editcomp" :title="S.edit" @click="editConfirm('comp')">{{ S.edit }}</button></template>
                        </span>
                      </div>
                      <div class="mc-cfm-actions">
                        <button class="mc-onb-go mc-onb-gofull mc-cfm-go" type="button" @click="confirmGo(false)">{{ S.confirmGo }}</button>
                        <button class="mc-cfm-cancel" type="button" @click="intakeConfirmCancel">{{ S.confirmCancel }}</button>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else-if="message.special === 'onboard'" class="mc-cardwrap">
                  <div class="mc-onb mc-enter">
                    <div class="mc-onb-spine"></div>
                    <div class="mc-onb-in">
                      <div class="mc-onb-lab">{{ S.onboardCardLab }}</div>
                      <div class="mc-onb-f"><span class="mc-onb-l">{{ S.onbBrand }}</span><input v-model="message.ui.brand" class="mc-onb-field" type="text" autocomplete="off" :placeholder="S.onbBrandPh"></div>
                      <div class="mc-onb-f"><span class="mc-onb-l">{{ S.onbCat }}</span><input v-model="message.ui.cat" class="mc-onb-field" type="text" autocomplete="off" :placeholder="S.onbCatPh"></div>
                      <div class="mc-onb-f"><span class="mc-onb-l">{{ S.onbUrl }}<em>{{ S.onbOpt }}</em></span><input v-model="message.ui.url" class="mc-onb-field" type="text" autocomplete="off" placeholder="https://"></div>
                      <div class="mc-onb-f"><span class="mc-onb-l">{{ S.onbComp }}<em>{{ S.onbOpt }}</em></span><input v-model="message.ui.comp" class="mc-onb-field" type="text" autocomplete="off" :placeholder="S.onbCompPh"></div>
                      <div class="mc-onb-f"><span class="mc-onb-l">{{ S.onbSell }}<em>{{ S.onbSellOpt }}</em></span><input v-model="message.ui.sell" class="mc-onb-field" type="text" autocomplete="off" :placeholder="S.onbSellPh"></div>
                      <div class="mc-onb-f"><span class="mc-onb-l">{{ S.onbBuyer }}<em>{{ S.onbBuyerOpt }}</em></span><input v-model="message.ui.buyer" class="mc-onb-field" type="text" autocomplete="off" :placeholder="S.onbBuyerPh"></div>
                      <button class="mc-onb-go mc-onb-gofull" type="button" @click="onboardCardSubmit(message)">{{ S.onbGo }}</button>
                    </div>
                  </div>
                </div>
                <section v-else-if="isProfileUpdate(message)" class="mprofile-update" :aria-label="t('maya_profile_updated')">
                  <span class="mprofile-update-mark" aria-hidden="true"></span>
                  <div class="mprofile-update-copy">
                    <p>{{ t("maya_profile_updated") }}</p>
                    <strong>{{ profileUpdateBrand(message) }}</strong>
                    <ul v-if="profileUpdateFields(message).length" :aria-label="t('maya_profile_fields')">
                      <li v-for="field in profileUpdateFields(message)" :key="field">{{ field }}</li>
                    </ul>
                  </div>
                </section>
                <div v-else-if="message.action?.type === 'tool_event'" class="mtoolev"><span aria-hidden="true">&#128295;</span> {{ message.action.summary || message.content }}</div>
                <template v-else>
                  <!-- R16b · the ONE progress control: live one-liner while she works, a settled
                       door afterwards. The bouncing-dot capsule, the Reasoning drawer and the
                       "Maya is working…" status line it replaced are gone. -->
                  <MayaTrace
                    v-if="message.trace?.length || message.traceLive"
                    :rows="message.trace || []"
                    :live="Boolean(message.traceLive)"
                    :label="message.traceLabel || ''"
                    :writing="Boolean(message.streamHasOutput)"
                    :reasoning="message.reasoning || ''"
                    :started-at="message.traceStartedAt || 0"
                    :answer-at="message.traceAnswerAt || 0"
                    :total-ms="message.traceTotalMs || 0"
                    :write-ms="message.traceWriteMs || 0"
                    :think-ms="message.traceThinkMs"
                    :render-reasoning="mdLite"
                  />
                  <div v-if="message.html || message.content || message.attachments?.length" class="mbub"><strong class="sr-only">{{ message.role === "user" ? t("comp_you") : "Maya" }}:</strong>
                    <div v-if="message.attachments?.length" class="mmsg-attachments" role="list" :aria-label="t('maya_message_attachments')">
                      <span v-for="filename in message.attachments" :key="filename" class="mmsg-attachment" role="listitem" :title="filename">{{ filename }}</span>
                    </div>
                    <div v-if="message.html" v-html="message.html"></div>
                    <p v-else-if="message.content">{{ message.content }}</p>
                  </div>
                  <MayaTraceChips v-if="message.trace?.length && !message.traceLive" :rows="message.trace" @open="openTraceChip" />
                  <MayaCard v-if="cardOf(message)" :card="cardOf(message)" :bus="cardBus" @action="runCardAction" />
                </template>
                <div v-if="chipsOf(message).length" class="machips">
                  <button v-for="(chip, i) in chipsOf(message)" :key="i" type="button" class="machip" :class="{ 'machip-primary': chip.__primary, 'machip-quiet': !chip.__primary }" :disabled="isCardActionBusy(chip)" :aria-busy="isCardActionBusy(chip) ? 'true' : undefined" @click="runChipAction(message, chip, i, $event)">{{ chip.label }}</button>
                </div>
                <div v-if="message.action?.type === 'plan_approve'" class="machip-fine"><span v-for="line in planFineLines" :key="line">{{ line }}</span></div>
                <div v-if="outreachPanels[message.id]" class="obpanel">
                  <div v-if="outreachPanels[message.id].state === 'loading'" class="obload" role="status"><span class="radar2" aria-hidden="true"></span><span>{{ t("ob_loading") }}</span></div>
                  <div v-else-if="outreachPanels[message.id].state === 'error'" class="oberr" role="alert">{{ outreachPanels[message.id].text }}</div>
                  <template v-else>
                    <div class="obhead"><span class="obl">{{ outreachPanels[message.id].cached ? t("ob_cached") : t("ob_fresh") }}</span><a v-if="outreachPanels[message.id].mailto" class="obmail" :href="outreachPanels[message.id].mailto">{{ t("ob_open_mail") }}</a><button type="button" class="obcp" @click="copyOutreach(message)">{{ outreachPanels[message.id].copied ? t("ob_copied_mail") : t("ob_copy") }}</button></div>
                    <div class="obbody" v-html="outreachPanels[message.id].html"></div>
                  </template>
                </div>
              </div>
            </li>
            <li v-if="buildPrep" class="mmsg maya">
              <div class="mav" aria-hidden="true"><img class="mavimg" :src="mayaImage" alt=""></div>
              <div class="mcol"><MayaBuildPrep :brand="buildPrep.brand" :busy="sending" @start="startPreparedBuild" /></div>
            </li>
            <li v-if="articleStage" class="mmsg maya">
              <div class="mav" aria-hidden="true"><img class="mavimg" :src="mayaImage" alt=""></div>
              <div class="mcol">
                <MayaArticleCard
                  :stage="articleStage"
                  :build-title="articleBuildTitle"
                  v-model:plan-text="articlePlanText"
                  :draft="articleDraft"
                  :brand="articleCtx.brand || currentBrand?.brand || ''"
                  :busy="articleBusy"
                  :unconf="articleUnconf"
                  :pub-line="articlePubLine"
                  :copied="articleCopied"
                  :body-html="articleBodyHtml"
                  :maya-image="mayaImage"
                  @generate="writePlannedArticle"
                  @regen="regenerateArticlePlan"
                  @publish="publishPlannedArticle"
                  @copy="copyArticleBody"
                  @workbench="openArticleWorkbench"
                  @ask-fill="askArticleFill"
                />
              </div>
            </li>
          </ol>
          <div v-if="scan" class="mc-cardwrap">
            <div class="mc-file mc-build mc-diagbuild" :style="{ '--fc': scan.state === 'fail' ? 'var(--mute)' : 'var(--indigo)' }">
              <template v-if="scan.state === 'run'">
                <div class="mc-fico"><span class="mc-spin"></span></div>
                <div class="mc-fmeta">
                  <div class="mc-ft">{{ S.scanTitle }}<span v-if="scan.brand" class="mc-kind"> {{ scan.brand }}</span></div>
                  <div class="mc-fs mc-step">{{ scanStepText }}</div>
                  <div class="mc-fs mc-eta" style="opacity:.68;margin-top:2px">{{ S.scanEta }}</div>
                </div>
                <div class="mc-buildbar"><i></i></div>
              </template>
              <template v-else-if="scan.state === 'ok'">
                <div class="mc-fico" style="background:rgba(14,138,109,.14)"><svg class="mc-okchk" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg></div>
                <div class="mc-fmeta">
                  <div class="mc-ft">{{ S.scanDoneTitle }} 🎉<span v-if="scan.brand" class="mc-kind"> {{ scan.brand }}</span></div>
                  <div class="mc-fs mc-step" style="color:var(--jade);font-weight:650">{{ S.scanDoneStep }}</div>
                </div>
              </template>
              <template v-else>
                <div class="mc-fmeta">
                  <div class="mc-ft">{{ S.scanFailTitle }}</div>
                  <div class="mc-fs mc-step" style="color:var(--mute)">{{ S.scanFailStep }}</div>
                </div>
              </template>
            </div>
          </div>
          <form class="maya-input" @submit.prevent="send()">
            <div v-if="pendingAttachments.length || attachmentBusy || attachmentError" class="maya-attachments" role="status" aria-live="polite">
              <div v-if="attachmentBusy" class="maya-attachment-status">{{ t("maya_attachment_uploading") }}</div>
              <div v-if="attachmentError" class="maya-attachment-error" role="alert">{{ attachmentError }}</div>
              <div v-for="attachment in pendingAttachments" :key="attachment.id" class="maya-attachment">
                <span class="maya-attachment-name" :title="attachment.file.name">{{ attachment.file.name }}</span>
                <span class="maya-attachment-size">{{ formatAttachmentSize(attachment.file.size) }}</span>
                <button type="button" class="maya-attachment-remove" :aria-label="t('maya_attachment_remove', { name: attachment.file.name })" @click="removeAttachment(attachment.id)">×</button>
              </div>
            </div>
            <p v-if="chatLocked" class="mc-chatgate" role="status">{{ S.chatGate }} <router-link to="/account/billing">{{ t("maya_see_plans") }}</router-link></p>
            <div class="mform">
              <button type="button" class="mc-palbtn" :title="t('maya_skills')" :aria-label="t('maya_skills')" :aria-expanded="paletteOpen ? 'true' : 'false'" aria-controls="mc_drawer" @click="openPalette"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M13 2L5.5 13.5H11L10 22L18.5 10H12.5Z" /></svg></button>
              <input ref="attachmentInput" class="sr-only" type="file" multiple accept=".md,.markdown,.txt,.docx,.pdf,.html,.htm,.png,.jpg,.jpeg,.webp" :disabled="sending || articleBusy || attachmentBusy" @change="onAttachmentChange">
              <label for="maya-input" class="sr-only">{{ t("maya_input_label") }}</label>
              <textarea id="maya-input" ref="input" v-model="draft" rows="1" :required="!pendingAttachments.length" :disabled="sending || articleBusy || attachmentBusy" :placeholder="chatLocked ? t('maya_ph_locked') : t('maya_ph')" @input="growInput" @keydown="inputKeydown"></textarea>
              <button id="maya_sendbtn" :type="sending ? 'button' : 'submit'" class="mc-sendbtn" :disabled="articleBusy" :aria-label="sending ? t('maya_stop') : t('maya_send_aria')" :title="sending ? t('maya_stop') : t('maya_send')" @click="sending ? stopMaya() : null">
                <svg v-if="!sending" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 19.5V5.5" /><path d="M5.5 12l6.5-6.5 6.5 6.5" /></svg><svg v-else viewBox="0 0 24 24" aria-hidden="true"><rect x="7" y="7" width="10" height="10" rx="1" /></svg>
              </button>
            </div>
          </form>
          <button v-if="showLatest" type="button" id="maya_tolatest" class="maya-tolatest" @click="scrollLatest">{{ t("maya_latest") }}</button>
        </div>
      </div>

      <div id="mc_drawer" class="mc-drawer" :class="{ on: paletteOpen }" :aria-hidden="paletteOpen ? 'false' : 'true'" @click.self="closePalette()">
        <div ref="paletteEl" class="mc-palette" role="dialog" aria-modal="true" aria-labelledby="mc-palette-title" @keydown="paletteKeydown">
          <div id="mc-palette-title" class="mc-phead"><img :src="mayaImage" alt="" aria-hidden="true"> {{ t("maya_skills_title") }}</div>
          <section v-for="group in skillGroups" :key="group.id" class="mc-skillgroup" :aria-labelledby="group.label ? `mc-skillgroup-${group.id}` : undefined" :aria-label="group.label ? undefined : t('maya_sk_add_brand')">
            <h2 v-if="group.label" :id="`mc-skillgroup-${group.id}`" class="mc-pgroup">{{ group.label }}</h2>
            <div class="mc-grid">
              <button v-for="skill in group.skills" :key="skill.id" type="button" class="mc-skill" :class="{ 'mc-skill-locked': skill.locked }" :disabled="skill.action === 'attach' && (sending || articleBusy || attachmentBusy)" @click="useSkill(skill, $event)"><span class="mc-skill-icon" aria-hidden="true">{{ skill.icon }}</span><span class="mc-skill-copy"><b>{{ skill.label }}<span v-if="skill.locked" class="mc-skill-lock">{{ t("maya_sk_locked") }}</span></b><small>{{ skill.locked ? t("maya_sk_locked_h") : skill.help }}</small></span></button>
            </div>
          </section>
          <button type="button" class="mc-close" @click="closePalette()">{{ t("wb_close") }}</button>
        </div>
      </div>

      <aside class="wb" :class="{ on: workbenchOpen, 'wb-wide': workbenchWide }" id="wb_drawer" role="complementary" :aria-label="t('wb_title')" :aria-hidden="workbenchOpen ? 'false' : 'true'">
        <div class="wb-head"><button type="button" class="wb-back" @click="closeWorkbench"><span aria-hidden="true">←</span> {{ t("wb_back") }}</button><div class="wb-ttl"><b>{{ workbenchTitle }}</b><span v-if="workbenchKind" class="wb-kind">{{ workbenchKind }}</span></div><button type="button" class="wb-x" :aria-label="t('wb_close')" @click="closeWorkbench">×</button></div>
        <div v-if="workbench?.type === 'sitewb'" class="wb-body"><div ref="wsRoot" class="ws-mount"></div></div>
        <MayaWorkbench v-else-if="workbench" :key="wbKey" :wb="workbench" :brand="currentBrand?.brand || ''" :brand-id="currentBrand?.id ?? null" @open="openWorkbenchFromPayload" @meta="onWbMeta" @bubble="onWbBubble" @say="say" @ask="workbenchAsk" @upgrade="upgradeBubble" @close="closeWorkbench" />
      </aside>
    </section>
  </AppShell>
</template>

<script setup>
import { computed, defineComponent, h, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { api, apiPrefix, assetUrl } from "../api/client";
import { draftBrandOutreach } from "../api/brands";
import { ensureMaya, getMayaHistory, getMayaSkillStatus, logMayaMessage, runMayaSkill, uploadMayaAttachment } from "../api/maya";
import { applySiteEdit, discardSiteEdit, getPendingSiteEdit } from "../api/siteEdits";
import { listSites } from "../api/sites";
import BrandNextAction from "../components/BrandNextAction.vue";  // GEO_EXEC_R18
import MayaArticleCard from "../components/MayaArticleCard.vue";
import MayaBuildPrep from "../components/MayaBuildPrep.vue";
import MayaTrace from "../components/MayaTrace.vue";
import MayaTraceChips from "../components/MayaTraceChips.vue";
import MayaWorkbench from "../components/MayaWorkbench.vue";
import { useMayaArticle } from "../composables/useMayaArticle";
import AppShell from "../layout/AppShell.vue";
import { MC_CAT, actionNode, blockNode, iconNode, obCopyStr, obMailtoStr, obParse, valueText } from "../legacy/mayaBlocks";
import { mountSiteWorkbench } from "../legacy/siteWorkbench.js";
import {
  AKIND_SKILL, Q3_COUNTRIES, T, bLocalCat, brandFromDomain, brandPinMessage, guessDomain, isCommand,
  isIntakeCancel, isSkipReply, mdLite, mayaStream, stopMayaStream, normalizeBrandAsk, parseRegion, q3MatchCountry,
  regionText, wantsNewScan,
} from "../legacy/mayaChat.js";
import { soaToast } from "../legacy/soaToast.js";
import { shell } from "../shellState";
import { diagnosisStatus, onboardScan, runBrand } from "../api/brands";
import { brandState, currentBrand, findBrandByName, loadBrands, selectBrand, selectBrandByName, setBrandFlow } from "../stores/brands";
import { onPlanExecutionCompleted } from "../stores/planExecutions.js";
import { session } from "../stores/session";
import { useI18n } from "../useI18n";
import { usePolling } from "../composables/usePolling";

const { t, lang: i18nLang, bandLabel } = useI18n();

function seStateLabel(state) {
  return t(state === "applied" ? "se_applied" : state === "discarded" ? "se_discarded" : state === "stale" ? "se_stale" : "se_gone");
}

function prevStateLabel(state) { return t(state === "keep" ? "prev_kept" : state === "reset" ? "prev_reset" : "prev_gone"); }

const MayaCard = defineComponent({
  name: "MayaCard",
  props: { card: { type: Object, required: true }, bus: { type: Object, default: null } },
  emits: ["action"],
  setup(props, { emit }) {
    const open = ref(Boolean(props.card.expanded || props.card.siteedit_token));
    const wrap = ref(null);
    const toggle = () => {
      open.value = !open.value;
      if (!open.value) return;
      nextTick(() => {
        const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        wrap.value?.scrollIntoView?.({ block: "end", behavior: reduce ? "auto" : "smooth" });
      });
    };
    return () => {
      const card = props.card;
      const zh = card.lang === "zh" || i18nLang.value === "zh";
      const fc = MC_CAT[card.category] || MC_CAT.deliverable;
      return h("div", { class: "mc-cardwrap", ref: wrap }, [
        h("button", { type: "button", class: ["mc-file", zh ? "mc-zh" : "", open.value ? "mc-open" : ""], style: { "--fc": fc }, onClick: toggle }, [
          h("div", { class: "mc-fico" }, [iconNode(card.icon || card.category || "scan")]),
          h("div", { class: "mc-fmeta" }, [h("div", { class: "mc-ft" }, [valueText(card.title || t("mc_result")), card.kind ? h("span", { class: "mc-kind" }, valueText(card.kind)) : null]), h("div", { class: "mc-fs" }, valueText(card.subtitle || ""))]),
          h("div", { class: "mc-fopen" }, [t("mc_open"), iconNode("chevron")]),
        ]),
        open.value ? h("div", { class: ["mc-card", zh ? "mc-zh" : ""], style: { "--fc": fc } }, [
          h("div", { class: "mc-spine" }),
          h("div", { class: "mc-attr" }, [h("img", { class: "mc-attrav", src: mayaImage, alt: "" }), h("span", { class: "mc-who" }, [valueText(card.attr?.who || "Maya"), card.attr?.role ? h("span", ` ${valueText(card.attr.role)}`) : null]), card.attr?.time ? h("span", { class: "mc-t" }, valueText(card.attr.time)) : null]),
          h("div", { class: "mc-pad" }, [
            card.eye ? h("div", { class: "mc-eye" }, [h("span", { class: "mc-dt" }), valueText(card.eye), card.eyeObj ? h("span", { class: "mc-obj" }, ` ${valueText(card.eyeObj)}`) : null]) : null,
            ...(card.body || []).map((block, i) => blockNode(block, i, null, props.bus)),
            card._seState
              ? h("span", { class: "mc-senote mc-se-" + card._seState }, seStateLabel(card._seState))
              : card._prevResolved
                ? h("span", { class: "mc-senote mc-prev-" + card._prevResolved }, prevStateLabel(card._prevResolved))
                : card.acts?.length ? h("div", { class: "mc-acts" }, card.acts.map((action, i) => actionNode(action, (a, event) => emit("action", a, event), i, Boolean(props.bus?.isActionBusy?.(action))))) : null,
          ]),
          h("div", { class: "mc-stamp" }, [h("span", { class: "mc-s" }, "S"), valueText(card.stamp || t("mc_verified"))]),
        ]) : null,
      ]);
    };
  },
});


const route = useRoute();
const router = useRouter();
const polling = usePolling();
const messages = ref([]);
const draft = ref("");
const loading = ref(true);
const sending = ref(false);
const error = ref("");
const input = ref(null);
const attachmentInput = ref(null);
const pendingAttachments = ref([]);
const attachmentBusy = ref(false);
const attachmentError = ref("");
let attachmentId = 0;
const threadEl = ref(null);
const paletteOpen = ref(false);
const paletteEl = ref(null);
const noReport = ref(false);
const showIntro = ref(false);
const showLatest = ref(false);
const workbench = ref(null);
const wsRoot = ref(null);
let wsHandle = null;
let workbenchTrigger = null;
let paletteTrigger = null;
let pendingSiteEdit = null;
let scanProgress = null;
let messageId = 0;
let wbQueryOpened = false;
let taskQueryOpened = false;
let pendingFaqItem = null;   // a generated FAQ {q,a} rides with the next skill sentence (old MAYA._faqItem)
let unsubscribePlanExecution = null;

// ── pre-Vue conversational state (ported MAYA flags) ──────────────────────────────────────────
const S = computed(() => T(session.language));
const histRaw = ref([]);          // full server history; the thread shows the active brand's slice
const intake = ref(null);         // MAYA._intake — pre-scan calibration, persisted to soa_onb_intake
const scan = ref(null);           // the running/failed diagnosis building card
const scanGen = ref(0);           // a newer scan supersedes an older poll loop
const freshConversation = ref(false); // add-brand/intake owns a clean thread; rbar hidden until done
const pendingName = ref("");      // MAYA._scanbrand — brand that is scanning but not yet registered
const suspendedBrandFlow = ref(null); // an in-progress add-brand flow paused while another brand is selected
const siteLookup = ref(null);     // MAYA._siteLookup — background brand→website discovery
const hiddenMsgs = ref([]);       // hidden prompts (greet / post-scan recap) ride the agent context
const buildPrep = ref(null);
const sitesByBrandId = ref({});
const outreachPanels = reactive({});
const cardActionBusy = reactive({});
const brandAskVal = ref("");
const confirmEdit = reactive({ brand: false, site: false, sell: false, buyer: false, region: false, cat: false, comp: false });
const confirmVals = reactive({ brand: "", site: "", sell: "", buyer: "", region: "", cat: "", comp: "" });
const SCAN_POLL_MS = 10000, SCAN_POLL_MAX = 180;   // one status request every 10s, up to 30 minutes

function lsGet(key) { try { return localStorage.getItem(key); } catch { return null; } }
function lsSet(key, value) { try { localStorage.setItem(key, value); } catch { /* storage optional */ } }
function lsRemove(key) { try { localStorage.removeItem(key); } catch { /* storage optional */ } }
function lsJson(key) { try { return JSON.parse(lsGet(key) || "null"); } catch { return null; } }

// A Free Scan customer can type a whole question before finding out Maya is paid; say it first.
const chatLocked = computed(() => session.canChat === false);
// F-402 (mystery round 2): and the same for the skills that BUILD something. "The UI called this
// 'Publish a page' without disclosing the Autopilot paywall, then blocked it after I selected the
// only relevant cluster" — the plan is known before the click, so the lock belongs on the button.
// `canExec` is `billing.execution_allowed`, the same entitlement every site route gates on.
const execLocked = computed(() => session.canExec === false);
const initials = computed(() => String(session.user?.name || session.user?.email || "?").slice(0, 1).toUpperCase());
const mayaImage = assetUrl("/assets/img/maya-256.webp");
const mayaLargeImage = assetUrl("/assets/img/maya-512.webp");
const JOB_KEY = "soa_skill_job";
const THREAD_KEY = "soa_maya_thread";
let skillAbort = null;
let turnSeq = 0;
const workbenchOpen = computed(() => Boolean(workbench.value));
const workbenchWide = computed(() => workbench.value?.type === "sitewb");
const regionCityRow = computed(() => Boolean(intake.value && bLocalCat(intake.value.cat)));
const scanStepText = computed(() => {
  if (!scan.value) return "";
  if (scan.value.step && S.value.scanSteps[scan.value.step]) return S.value.scanSteps[scan.value.step];
  return S.value.scanStep0;
});
const workbenchTitle = computed(() => {
  const wb = workbench.value;
  if (!wb) return t("wb_title");
  if (wb.type === "sitewb") return t("wb_title");
  if (wb.title) return wb.title;
  if (wb.type === "plan90") return t("wb_p90_title");
  if (wb.type === "compare") return t("wb_cmp_title");
  if (wb.type === "plan") return t("wb_plan_title");
  if (wb.type === "site") return t("wb_preview");
  return t("wb_title");
});
const workbenchKind = computed(() => {
  const wb = workbench.value;
  if (!wb) return "";
  if (wb.type === "sitewb") return t("wb_kind_site");
  if (wb.type === "plan90") return t("wb_p90_kind");
  if (wb.type === "report") return t("wb_kind_report");
  if (wb.type === "compare") return t("wb_kind_cmp");
  if (wb.type === "plan") return t("wb_kind_plan");
  if (wb.type === "site") return t("wb_kind_site");
  if (wb.type === "deliverable") return wb.kind && wb.kind !== "wbr_kind" ? wb.kind : t("wb_kind_article");
  if (wb.kind === "wbr_kind") return t("wbr_kind");
  if (wb.type === "task") return t("wb_kind_task");
  return wb.type || "";
});
const wbKey = computed(() => {
  const wb = workbench.value;
  if (!wb) return "";
  return `${wb.type}:${wb.slug ?? wb.brand ?? wb.id ?? ""}`;
});
const rbarBrand = computed(() => (freshConversation.value ? null : currentBrand.value || null));
const rbarLatest = computed(() => rbarBrand.value?.latest || null);
const currentSite = computed(() => sitesByBrandId.value[String(currentBrand.value?.id)] || null);
const currentSiteExists = computed(() => ["draft", "published"].includes(currentSite.value?.status));
const showReportBar = computed(() => Boolean(rbarBrand.value) && !showIntro.value && !noReport.value && !scan.value && (Boolean(rbarLatest.value) || !intake.value));   // calibration hides only the "run one" CTA (mayaRBar), not an existing report
const skillGroups = computed(() => [
  {
    id: "site", label: t("maya_sk_group_site"), skills: [
      { id: "site-build", action: "build", icon: "🌐", label: currentSiteExists.value ? t("wb_p90_edit_site") : t("maya_sk_build"), help: currentSiteExists.value ? t("maya_sk_edit_h") : t("maya_sk_build_h"), locked: execLocked.value },
      { id: "site-edit", action: "ask", icon: "✎", label: t("maya_sk_edit"), help: t("maya_sk_edit_h"), text: t("maya_sk_edit_q"), locked: execLocked.value },
      { id: "site-publish", action: "ask", icon: "↗", label: t("maya_sk_publish"), help: t("maya_sk_publish_h"), text: t("maya_sk_publish_q"), locked: execLocked.value },
      { id: "site-content", action: "card", skill: "content", icon: "✍", label: t("maya_sk_site_content"), help: t("maya_sk_site_content_h"), text: t("maya_sk_site_content_q"), locked: execLocked.value },
      { id: "site-audit", action: "card", skill: "audit", icon: "🕸", label: t("maya_sk_audit"), help: t("maya_sk_audit_h"), text: t("maya_sk_cw_q") },
    ],
  },
  {
    id: "diagnosis", label: t("maya_sk_group_diagnosis"), skills: [
      { id: "diagnosis", action: "card", skill: "diagnosis", icon: "📊", label: t("maya_sk_diag"), help: t("maya_sk_diag_h"), text: t("maya_sk_diag_q") },
      { id: "prioritize", action: "card", skill: "prioritize", icon: "⚑", label: t("maya_sk_prioritize"), help: t("maya_sk_prioritize_h"), text: t("maya_sk_report_q") },
      { id: "competitors", action: "card", skill: "competitors", icon: "🏁", label: t("maya_sk_comp"), help: t("maya_sk_comp_h"), text: t("maya_sk_comp_q") },
      { id: "citations", action: "card", skill: "citations", icon: "🔗", label: t("maya_sk_cit"), help: t("maya_sk_cit_h"), text: t("maya_sk_cit_q") },
      { id: "questions", action: "card", skill: "questions", icon: "☷", label: t("maya_sk_questions"), help: t("maya_sk_questions_h"), text: t("maya_sk_pr_q") },
      { id: "sentiment", action: "card", skill: "sentiment", icon: "💬", label: t("maya_sk_sentiment"), help: t("maya_sk_sentiment_h"), text: t("maya_sk_sen_q") },
    ],
  },
  {
    id: "growth", label: t("maya_sk_group_growth"), skills: [
      { id: "outreach", action: "card", skill: "outreach", icon: "✉", label: t("maya_sk_outreach"), help: t("maya_sk_outreach_h"), text: t("maya_sk_outreach_q") },
      { id: "trends", action: "card", skill: "trends", icon: "↗", label: t("maya_sk_trends"), help: t("maya_sk_trends_h"), text: t("maya_sk_trends_q") },
      { id: "watch", action: "card", skill: "watch", icon: "◌", label: t("maya_sk_watch"), help: t("maya_sk_watch_h"), text: t("maya_sk_watch_q") },
    ],
  },
  {
    id: "attachments", label: null, skills: [
      { id: "attach-file", action: "attach", icon: "📎", label: t("maya_attachment_add"), help: t("maya_attachment_add_h") },
    ],
  },
  {
    id: "add-brand", label: null, skills: [
      { id: "add-brand", action: "add", icon: "+", label: t("maya_sk_add_brand"), help: t("maya_sk_add_brand_h") },
    ],
  },
]);
const planFineLines = computed(() => session.language === "zh"
  ? ["已包含在你的套餐里 — 不额外收费", "活儿她来做;需要时你只提供材料", "没有你的同意,任何东西都不会上线"]
  : session.language === "ja"
    ? ["プランに含まれます — 追加料金なし", "作業は彼女が担当 — 必要な時だけ素材を", "あなたのOKなしに公開されるものはありません"]
    : ["Included in your plan — no extra charge", "She does the work; you only supply materials when asked", "Nothing goes live without your OK"]);

function normalizeMessage(message) {
  const action = message.action || (message.card ? { card: message.card } : null);
  // R16b · a replayed turn reads its trace and its reasoning summary off `action`, where the
  // gateway stored them. Turns written before R16b have neither and replay exactly as before.
  const trace = message.trace || (Array.isArray(action?.trace) ? action.trace : null);
  const stored = (action && typeof action.trace_ms === "object" && action.trace_ms) || null;
  const replay = trace
    ? {
      trace,
      reasoning: message.reasoning || action?.reasoning || "",
      traceTotalMs: message.traceTotalMs || stored?.total || traceStoredMs(trace),
      traceWriteMs: message.traceWriteMs || stored?.write || 0,
      traceThinkMs: message.traceThinkMs ?? stored?.thinking,
    }
    : {};
  return { ...message, ...replay, id: ++messageId, action };
}
// The fallback for a turn stored before `action.trace_ms` existed: the sum of what the server timed
// per tool. It undercounts — it cannot know about the gaps — which is exactly why the gateway now
// stores the real numbers instead.
function traceStoredMs(rows) { return (rows || []).reduce((total, row) => total + (row.ms || 0), 0); }
function cardOf(message) { return message.action?.card || message.card || null; }
function isProfileUpdate(message) { return message.action?.type === "tool_event" && message.action.tool === "update_profile"; }
function profileUpdateBrand(message) { return message.action?.brand || message.brand || t("prof_h"); }
function profileUpdateFields(message) {
  const fields = message.action?.field_labels || message.action?.fields;
  if (!Array.isArray(fields)) return [];
  return fields.map((field) => String(field || "").replaceAll("_", " ").replace(/^./, (letter) => letter.toUpperCase())).filter(Boolean);
}
function chipsOf(message) {
  if (Array.isArray(message.chips) && message.chips.length) return message.chips;
  const action = message.action;
  if (!action || action.card) return [];
  if (action.type === "acts" && Array.isArray(action.acts)) return action.acts.map((a, i) => ({ ...a, __primary: a.kind === "cta" && i === 0 }));
  if (action.type === "view") return [{ view: action.view || "competitors", __primary: true, label: t("ma_view", { v: viewLabel(action.view) }) }];
  if (action.type === "studio") return [{ studio: true, brand_id: action.brand_id, __primary: true, label: t("ma_open_wb") }];
  if (action.type === "deliv") return (action.items?.length ? action.items : action.slug ? [{ slug: action.slug, title: action.title }] : []).map((item) => ({ deliverable: item, __primary: true, label: item.title || t("wb_open_here") }));
  if (action.type === "taskwb" && action.task_id) return [{ task_id: action.task_id, __primary: true, label: t("wbt_open") }];
  if (action.type === "plan_approve") return [{ planBuild: action.brand || "", __primary: true, label: t("wb_p90_build") }, { planReport: action.brand || "", label: t("wb_p90_report") }];
  if (action.type === "outreach" && action.domain && action.brand_id) return [{ outreach: true, __primary: true, label: t("ob_btn") }];
  return [];
}
function viewLabel(view) { return t(({ overview: "nav_dashboard", add: "nav_add", plan: "nav_plan", files: "nav_files", alerts: "nav_alerts", reports: "nav_reports", sites: "nav_sites", crawlers: "nav_crawlers", competitors: "nav_competitors", citations: "nav_citations", prompts: "nav_prompts", sentiment: "nav_sentiment" })[view] || "nav_dashboard"); }
function pushMessage(message) { messages.value.push({ id: ++messageId, ...message }); nextTick(scrollLatest); return messages.value[messages.value.length - 1]; }
function say(text) { return pushMessage({ role: "assistant", content: text, html: mdLite(text) }); }
// R16b · "she is working" is ONE line now: the trace header in its live state. The 3×3
// bouncing-dot capsule this used to push is gone, and with it the bubble it lived in — an empty `html` means no
// `.mbub` renders at all, so a turn in flight is exactly one row tall. When the answer arrives the
// caller clears `traceLive`; a turn that used no tools then has nothing left to render and the line
// simply disappears, which is what a 0-lookup turn should do.
function mayaThinking(text = t("maya_trace_thinking")) {
  return pushMessage({ role: "assistant", content: "", html: "", traceLive: true, traceLabel: text, traceStartedAt: Date.now() });
}
// R16: tool rows arrive as running → done|error for the same call_id, so they are merged in place
// rather than appended — two calls to the same tool in one turn stay two rows, one call stays one.
function applyTraceRow(message, row) {
  if (!row || !row.call_id) return;
  if (!message.trace) message.trace = [];
  const existing = message.trace.find((item) => item.call_id === row.call_id);
  if (existing) Object.assign(existing, row);
  else message.trace.push({ ...row, startedAt: Date.now() });
}
// The server flushes its own rows at the end of a clean stream; a transport error, a watchdog
// abort or a server-side error never reaches that code, so the browser closes any row still
// spinning on its own — and it closes them the way the turn actually ended. A row that was still
// in flight when the turn broke was never proven to have succeeded, so it must not be painted
// green; only a clean finish or the customer's own stop settles it as done.
function settleTrace(message, outcome) {
  const state = outcome === "err" ? "error" : "done";
  for (const row of message.trace || []) {
    if (row.state !== "running") continue;
    row.state = state;
    row.ms = row.ms || Math.max(0, Date.now() - (row.startedAt || Date.now()));
  }
  // R16b · the turn is over: the live line settles into "Thought for Ns · N lookups" and the two
  // durations are frozen here so a reload replays the numbers the customer actually saw.
  message.traceLive = false;
  if (message.traceStartedAt) message.traceTotalMs = Math.max(0, Date.now() - message.traceStartedAt);
  if (message.traceAnswerAt) message.traceWriteMs = Math.max(0, Date.now() - message.traceAnswerAt);
}
// R16b · `{ total, write, thinking }` in milliseconds, straight from the gateway.
function applyTraceMs(message, timings) {
  if (!timings || typeof timings !== "object") return;
  if (Number.isFinite(timings.total)) message.traceTotalMs = timings.total;
  if (Number.isFinite(timings.write)) message.traceWriteMs = timings.write;
  if (Number.isFinite(timings.thinking)) message.traceThinkMs = timings.thinking;
}
function removeMessage(message) { const index = messages.value.indexOf(message); if (index >= 0) messages.value.splice(index, 1); }
function saveJob(job) { try { sessionStorage.setItem(JOB_KEY, JSON.stringify({ job, created: Date.now() })); } catch { /* optional storage */ } }
function clearJob() { try { sessionStorage.removeItem(JOB_KEY); } catch { /* optional storage */ } }
function savedJob() { try { const value = JSON.parse(sessionStorage.getItem(JOB_KEY) || "null"); return value && Date.now() - value.created < 2 * 60 * 60 * 1000 ? value.job : null; } catch { return null; } }
// R16b · the gateway stamps `status.started_at` with the moment the RUN began (epoch seconds),
// which is earlier than the moment this browser's fetch resolved — connecting to Hermes happens in
// between, and on a slow node that is seconds of a turn the customer waited through. Reading a
// server epoch against Date.now() would report whatever the two clocks disagree by, so the offset
// is measured once, on the first turn of the session, and reused: from then on every turn is
// anchored at the server's own start rather than at this browser's arrival time.
let serverClockOffset = null;
function serverEpochToLocal(seconds) {
  const ms = Number(seconds) * 1000;
  if (!Number.isFinite(ms) || ms <= 0) return Date.now();
  if (serverClockOffset === null) serverClockOffset = Date.now() - ms;
  return ms + serverClockOffset;
}
function persistThread() {
  try {
    const snapshot = messages.value
      .filter((message) => (message.role === "user" || message.role === "assistant") && (message.content || message.action || message.chips))
      .slice(-40)
      .map((message) => ({
        role: message.role,
        content: message.content || "",
        html: message.html || "",
        action: message.action || null,
        chips: message.chips || null,
        streamState: message.streamState === "working" ? undefined : message.streamState,
        // R16b · a session restore shows the same settled trace the server will hand back later.
        trace: message.trace || null,
        reasoning: message.reasoning || "",
        traceTotalMs: message.traceTotalMs || 0,
        traceWriteMs: message.traceWriteMs || 0,
        traceThinkMs: message.traceThinkMs,
      }));
    sessionStorage.setItem(THREAD_KEY, JSON.stringify({ brand: activeBrandForChat(), msgs: snapshot, at: Date.now() }));
  } catch { /* optional storage */ }
}
function localThread() {
  try {
    const value = JSON.parse(sessionStorage.getItem(THREAD_KEY) || "null");
    if (!value || !Array.isArray(value.msgs) || Date.now() - value.at > 24 * 60 * 60 * 1000) return null;
    return value;
  } catch { return null; }
}
function restoreLocalThread() {
  const local = localThread();
  if (!local?.msgs?.length) return false;
  const key = (pendingName.value || currentBrand.value?.brand || "").trim().toLowerCase();
  if (key && local.brand && String(local.brand).trim().toLowerCase() !== key) return false;
  if (local.msgs.length <= messages.value.length) return false;
  messages.value = local.msgs.map(normalizeMessage);
  return true;
}
function entityChoiceChips(text) {
  const source = String(text || "");
  if (!/监测|monitor|主体|entity/i.test(source)) return [];
  const quoted = [...source.matchAll(/[“"「『]([^“"」』\n]{1,40})[”"」』]/g)].map((match) => match[1].trim()).filter(Boolean);
  const unique = [...new Set(quoted)];
  if (unique.length < 2 || unique.length > 4) return [];
  return unique.map((name, index) => ({ label: name, entity: name, __primary: index === 0 }));
}
function lastOfferedEntities() {
  for (let i = messages.value.length - 1; i >= 0; i -= 1) {
    const message = messages.value[i];
    if (message.role !== "assistant") continue;
    const fromChips = (message.chips || []).map((chip) => chip.entity).filter(Boolean);
    if (fromChips.length) return fromChips;
    return entityChoiceChips(message.content || "").map((chip) => chip.entity);
  }
  return [];
}
function skillPayload(result) {
  if (!result || typeof result !== "object") return result;
  // Durable background jobs wrap the Maya response in `result`; the legacy in-process
  // job store returns the response directly. Keep both response shapes equivalent.
  const nested = result.result;
  return nested && typeof nested === "object" ? { ...nested, state: result.state || nested.state } : result;
}
async function resumeJob(job, pendingReply = null) {
  const progress = pendingReply || mayaThinking();
  try {
    const result = await polling.start(({ signal }) => getMayaSkillStatus(job, { signal }));
    clearJob();
    const payload = skillPayload(result);
    const rendered = Boolean(payload && (payload.intro || payload.msg || payload.content || payload.card || payload.upgrade));
    if (rendered) handleSkillResult(payload, progress);
    else {
      removeMessage(progress);
      if (payload?.state === "done") say(t("maya_finished"));
    }
  } catch (error) {
    removeMessage(progress);
    throw error;
  }
}
const article = useMayaArticle({
  t,
  lang: () => session.language,
  currentBrand,
  route,
  selectBrandByName,
  say,
  pushMessage,
  upgradeBubble: (msg, url) => upgradeBubble(msg, url),
  send: (...args) => send(...args),
  pushCard: (card) => pushCard(card),
  mdLite,
  toast: soaToast,
});
const articleStage = article.stage;
const articleBuildTitle = article.buildTitle;
const articlePlanText = article.planText;
const articleDraft = article.draft;
const articleCtx = article.ctx;
const articleBusy = article.busy;
const articleUnconf = article.unconf;
const articlePubLine = article.pubLine;
const articleCopied = article.copied;
const articleBodyHtml = computed(() => article.bodyHtml());
function startArticlePlan() { return article.startFromQuery(); }
function writePlannedArticle() { return article.writePlannedArticle(); }
function regenerateArticlePlan() { return article.regeneratePlan(); }
function publishPlannedArticle(force) { return article.publishArticle(force); }
function copyArticleBody() { article.copyBody(); }
function askArticleFill() { article.askToFill(); }
function openArticleWorkbench() {
  const data = article.draft.value;
  if (!data) return;
  workbench.value = { type: "deliverable", title: data.title || t("wb_title"), kind: t("wb_kind_article"), html: mdLite(data.body || ""), slug: data.slug };
}

// the interactive blocks (contentpick & friends in mayaBlocks) talk back through this bus — this
// view owns the article pipeline, the send loop and the brand cache; the blocks only know their bl
const cardBus = {
  articlePlan: (brand, q) => {
    void article.articlePlan({ brand, q, cn: "", layer: "", type: "", vol: 0, rec: "" });
    nextTick(scrollLatest);
  },
  ask: (sentence, faqItem) => {
    if (faqItem) pendingFaqItem = faqItem;   // the generated {q,a} rides with the sentence that routes it
    askSentence(sentence);
  },
  history: () => messages.value.slice(-6).map((m) => ({ role: m.role, content: m.content })),
  refreshBrands: () => { void loadBrands(); },
  isActionBusy: (action) => isCardActionBusy(action),
};

// ══ brand-scoped conversation (mayaTabFilter): the thread shows the active brand's slice ══
// Brandless messages (older turns, offer chips) stay visible in every thread.
function applyBrandFilter() {
  const key = (pendingName.value || currentBrand.value?.brand || "").trim().toLowerCase();
  const src = histRaw.value || [];
  const filtered = key ? src.filter((m) => !(m.brand || "").trim() || (m.brand || "").trim().toLowerCase() === key) : src;
  messages.value = filtered.map(normalizeMessage);
}

// A completed plan job writes Maya history on the server. Re-read that history instead of
// manufacturing a local completion bubble, so refreshes and route changes show the same result.
async function refreshMayaHistory() {
  const data = await getMayaHistory();
  histRaw.value = data.msgs || [];
  applyBrandFilter();
  await nextTick();
  settleToBottom();
}
function activeBrandForChat() {
  if (intake.value || freshConversation.value) return pendingName.value || "";
  return pendingName.value || currentBrand.value?.brand || "";
}
function suspendBrandFlowForSwitch() {
  const brand = String(pendingName.value || intake.value?.brand || scan.value?.brand || "").trim();
  if (intake.value) intakeSave();
  if (brand) suspendedBrandFlow.value = { brand: brand.toLowerCase() };
  else if (brandAskVal.value.trim()) lsSet("soa_onb_brandask", brandAskVal.value.trim());
  freshConversation.value = false;
  intake.value = null;
  scan.value = null;
  pendingName.value = "";
  setBrandFlow(false);
  hiddenMsgs.value = [];
  if (workbench.value) closeWorkbench();
}
watch(() => currentBrand.value?.id, () => {
  const selected = currentBrand.value;
  const pending = String(pendingName.value || "").trim().toLowerCase();
  // A new brand is not in the picker until its scan creates the row. Selecting an
  // existing brand while that flow is active must pause the flow and rethread Maya.
  if (selected && (freshConversation.value || intake.value || scan.value) && (!pending || String(selected.brand || "").trim().toLowerCase() !== pending)) {
    suspendBrandFlowForSwitch();
  }
  if (currentBrand.value && pendingName.value && String(currentBrand.value.brand).toLowerCase() === pendingName.value.trim().toLowerCase()) pendingName.value = "";
  // Brand switches always re-thread Maya. The paused add-brand flow is persisted
  // separately, so it can no longer block the selected brand's session.
  if (!freshConversation.value && !intake.value && !scan.value && !sending.value && !loading.value) {
    applyBrandFilter();
    void refreshMayaHistory().catch(() => {});
  }
});

// ══ pre-scan calibration — the pre-Vue intake, questions and cards verbatim ═══════════════════
function intakeSave() { if (intake.value) lsSet("soa_onb_intake", JSON.stringify(intake.value)); }
function intakeClear() { intake.value = null; for (const k of Object.keys(confirmEdit)) confirmEdit[k] = false; lsRemove("soa_onb_intake"); }
function removeSpecial(kind) { const i = messages.value.findIndex((m) => m.special === kind); if (i >= 0) messages.value.splice(i, 1); }
function siteLookupFire(brand, hint) {
  if (siteLookup.value?.__brand === (brand || "").toLowerCase()) return;
  siteLookup.value = { __brand: (brand || "").toLowerCase() };
  api("/api/site-lookup", { method: "POST", json: { brand, hint: hint || "" } })
    .then((d) => { if (d && typeof d === "object") siteLookup.value = { __brand: (brand || "").toLowerCase(), ...d }; })
    .catch(() => {});
}
function intakeBegin(brand, url, cat, comp) {
  // A new brand owns a clean Maya thread. Do not let the previous brand's
  // messages or local onboarding state leak into this calibration.
  freshConversation.value = true;
  suspendedBrandFlow.value = null;
  hiddenMsgs.value = [];
  messages.value = [];
  scan.value = null;
  pendingName.value = brand;
  lsRemove("soa_onb_bak");
  lsRemove("soa_pending_scan");
  setBrandFlow(true, brand);
  noReport.value = false; showIntro.value = false;
  intake.value = { stage: "q1", brand, url: url || "", cat: cat || "", comp: comp || "", answers: { sell: "", buyer: "", region: "", city: "" }, ui: {} };
  if (!(url || "").trim()) siteLookupFire(brand, "");
  say(S.value.intakeHi(brand));
  intakeSave();
  setTimeout(() => askQuestion("q1"), 650);
}
function selectExistingBrand(name) {
  const existing = findBrandByName(name);
  if (!existing) return null;
  selectBrand(existing.id);
  intakeClear();
  scan.value = null;
  pendingName.value = "";
  freshConversation.value = false;
  suspendedBrandFlow.value = null;
  setBrandFlow(false);
  noReport.value = false;
  showIntro.value = false;
  hiddenMsgs.value = [];
  messages.value = [];
  applyBrandFilter();
  return existing;
}
async function startExistingBrandRetest(name) {
  const existing = selectExistingBrand(name);
  if (!existing) return false;
  await rerunDiagnosis(existing.brand);
  return true;
}
function askQuestion(stage) {
  const it = intake.value;
  if (!it) return;
  it.stage = stage;
  it.qAt = Date.now();
  say(stage === "q1" ? S.value.q1 : S.value.q2);
  intakeSave();
}
function intakeDeflect() {
  const it = intake.value;
  if (!it) return;
  say(it.stage === "q3" ? S.value.deflectCard : S.value.deflect);
  if (it.stage === "q1" || it.stage === "q2") setTimeout(() => { if (intake.value) askQuestion(it.stage); }, 450);
}
function intakeAnswer(v) {
  const it = intake.value;
  if (!it) return;
  if (isCommand(v)) { intakeDeflect(); return; }
  if (it.stage === "q3") {
    // a typed country wins over the card default; the card itself is removed
    if (!isSkipReply(v)) { const m = q3MatchCountry(v); it.answers.region = (m || v).slice(0, 80); }
    if (it.ui && it.ui.city) it.answers.city = String(it.ui.city).trim().slice(0, 120);
    removeSpecial("region");
    intakeSave();
    intakeConfirm();
    return;
  }
  intakeAdvance(isSkipReply(v) ? null : v);
}
function intakeAdvance(answer) {
  const it = intake.value;
  if (!it) return;
  if (it.stage === "q1") {
    if (answer) it.answers.sell = answer.slice(0, 200);
    intakeSave();
    setTimeout(() => askQuestion("q2"), 450);
    return;
  }
  if (answer) it.answers.buyer = answer.slice(0, 200);
  intakeSave();
  setTimeout(askRegion, 450);
}
function askRegion() {
  const it = intake.value;
  if (!it) return;
  it.stage = "q3";
  it.qAt = Date.now();
  const lk = siteLookup.value && !siteLookup.value.__brandOnly ? siteLookup.value : (it.lookup || null);
  const guess = it.answers.region || (lk && lk.country) || "United States";
  const matched = q3MatchCountry(guess);
  it.ui = { country: matched || "__other__", other: matched ? "" : guess, city: it.answers.city || (lk && lk.city) || "" };
  say(S.value.q3Lead);
  pushMessage({ role: "assistant", special: "region" });
  intakeSave();
}
function regionGo() {
  const it = intake.value;
  if (!it || it.stage !== "q3") return;
  const raw = it.ui.country === "__other__" ? (it.ui.other || "").trim() : it.ui.country;
  it.answers.region = (raw || "").slice(0, 80);
  it.answers.city = (it.ui.city || "").trim().slice(0, 120);
  removeSpecial("region");
  intakeSave();
  intakeConfirm();
}
function editConfirm(field) {
  const it = intake.value;
  if (!it) return;
  confirmEdit[field] = true;
  if (field === "brand") confirmVals.brand = it.brand;
  if (field === "site") confirmVals.site = (it.confUrl || it.url || "").replace(/^https?:\/\//, "");
  if (field === "sell") confirmVals.sell = it.answers.sell || "";
  if (field === "buyer") confirmVals.buyer = it.answers.buyer || "";
  if (field === "region") confirmVals.region = regionText(it);
  if (field === "cat") confirmVals.cat = it.cat || "";
  if (field === "comp") confirmVals.comp = it.comp || "";
}
function intakeConfirm(resume) {
  const it = intake.value;
  if (!it) return;
  removeSpecial("confirm");
  it.stage = "confirm";
  const lk = (resume ? it.lookup : (siteLookup.value && siteLookup.value.url ? siteLookup.value : it.lookup)) || null;
  it.lookup = lk;
  const given = (it.url || "").trim();
  let url = "", src = "none";
  if (given) { url = given; src = "given"; }
  else if (lk && lk.url && lk.confidence === "high") { url = lk.url; src = "found"; }
  else if (lk && lk.url) { url = lk.url; src = "found_low"; }
  it.confUrl = url;
  it.confSrc = src;
  for (const k of Object.keys(confirmEdit)) confirmEdit[k] = false;
  confirmVals.site = src === "none" ? "" : src === "found_low" ? url : "";
  if (src === "none" || src === "found_low") confirmEdit.site = true;   // the site is the one open input
  pushMessage({ role: "assistant", special: "confirm" });
  intakeSave();
  nextTick(scrollLatest);
}
function confirmGo(auto) {
  const it = intake.value;
  if (!it || it.stage !== "confirm") return;
  // commit any in-progress row edits (mayaIntakeConfirmGo read the live inputs before launch)
  const bv = (confirmVals.brand || "").trim(); if (bv) it.brand = bv;
  it.answers.sell = (confirmVals.sell || "").trim().slice(0, 200);
  it.answers.buyer = (confirmVals.buyer || "").trim();
  parseRegion(it, confirmVals.region || "");
  it.cat = (confirmVals.cat || "").trim();
  it.comp = (confirmVals.comp || "").trim();
  let edited = (confirmVals.site || "").trim();
  if (it.confSrc === "none" || it.confSrc === "found_low" || confirmEdit.site) {
    if (edited && edited.indexOf("http") !== 0 && /[a-z0-9-]+\.[a-z]{2,}/i.test(edited)) edited = "https://" + edited.replace(/^\/+/, "");
    it.url = edited;   // the input being present is authoritative — empty means they deleted the site
  } else if (edited) {
    if (edited.indexOf("http") !== 0 && /[a-z0-9-]+\.[a-z]{2,}/i.test(edited)) edited = "https://" + edited.replace(/^\/+/, "");
    it.url = edited;
  } else {
    it.url = it.confUrl || it.url || "";
  }
  removeSpecial("confirm");
  intakeSave();
  intakeStartScan(true);   // the confirm card already echoed their words — no quote line (fromConfirm)
}
function intakeConfirmCancel() {
  const it = intake.value;
  if (!it || it.stage !== "confirm") return;
  removeSpecial("confirm");
  const brand = it.brand || "";
  intakeClear();
  setBrandFlow(false);
  say(brand ? S.value.cancelled(brand) : S.value.cancelledNoBrand);
  brandAsk();
}
function intakeStartScan(fromConfirm) {
  const it = intake.value;
  if (!it) return;
  const sell = it.answers.sell || "", buyer = it.answers.buyer || "";
  const quote = buyer || sell;
  if (quote && !fromConfirm) {
    const qs = quote.length > 64 ? quote.slice(0, 64) + "…" : quote;
    say(S.value.quoteGo(qs));
  }
  const region = it.answers.region || "", city = it.answers.city || "";
  const { brand, url, cat, comp } = it;
  intakeClear();
  startScan(brand, url, cat, comp, sell, buyer, fromConfirm || Boolean(quote), region, city);
}
function intakeResume(intk) {
  noReport.value = false; showIntro.value = false;
  setBrandFlow(true, intk.brand);
  suspendedBrandFlow.value = null;
  pendingName.value = intk.brand || "";
  if (intk.brand) selectBrandByName(intk.brand);
  intake.value = {
    stage: intk.stage, brand: intk.brand, url: intk.url || "", cat: intk.cat || "", comp: intk.comp || "",
    answers: { sell: (intk.answers && intk.answers.sell) || "", buyer: (intk.answers && intk.answers.buyer) || "", region: (intk.answers && intk.answers.region) || "", city: (intk.answers && intk.answers.city) || "" },
    lookup: intk.lookup || null, confUrl: intk.confUrl || "", confSrc: intk.confSrc || "", ui: {},
  };
  say(S.value.resumeLine);
  if (!(intake.value.url || "").trim()) siteLookupFire(intake.value.brand, "");
  if (intake.value.stage === "confirm") { intakeConfirm(true); return; }
  if (intake.value.stage === "q3") { askRegion(); return; }
  askQuestion(intake.value.stage);
}

// ══ add a brand (mayaNewTest + mayaBrandAsk): one card, then the same calibration ═════════════
function startAddBrandConversation() {
  if (freshConversation.value && (intake.value || scan.value || messages.value.some((message) => message.special === "brandask"))) {
    nextTick(() => input.value?.focus());
    return;
  }
  if (resumeInflightScan()) {
    noReport.value = false; showIntro.value = false;
    say(S.value.stillFinishing);
    return;
  }
  const savedIntake = lsJson("soa_onb_intake");
  if (savedIntake?.brand && savedIntake.stage) {
    setBrandFlow(true, savedIntake.brand);
    freshConversation.value = true;
    noReport.value = false; showIntro.value = false;
    messages.value = [];
    hiddenMsgs.value = [];
    intakeResume(savedIntake);
    nextTick(() => input.value?.focus());
    ensureMaya().catch(() => {});
    return;
  }
  const savedAsk = lsGet("soa_onb_brandask") || "";
  setBrandFlow(true, savedAsk);
  freshConversation.value = true;
  noReport.value = false; showIntro.value = false;
  intakeClear();
  scan.value = null;
  hiddenMsgs.value = [];
  messages.value = [];
  say(S.value.addHi);
  brandAsk();
  brandAskVal.value = savedAsk;
  lsRemove("soa_onb_brandask");
  nextTick(() => input.value?.focus());
  ensureMaya().catch(() => {});   // fire provisioning in the background — do not block entry
}
function startOverBrandConversation() {
  lsRemove("soa_onb_intake");
  lsRemove("soa_onb_brandask");
  lsRemove("soa_onb_bak");
  lsRemove("soa_onb");
  lsRemove("soa_onb_b");
  lsRemove("soa_onb_k");
  lsRemove("soa_onb_bid");
  lsRemove("soa_onb_ans");
  suspendedBrandFlow.value = null;
  scanGen.value += 1;
  intake.value = null;
  scan.value = null;
  pendingName.value = "";
  setBrandFlow(true);
  freshConversation.value = true;
  noReport.value = false;
  showIntro.value = false;
  hiddenMsgs.value = [];
  messages.value = [];
  if (route.query.intent !== "add-brand") router.replace({ path: "/maya", query: { intent: "add-brand" } }).catch(() => {});
  say(S.value.addHi);
  brandAsk();
  nextTick(() => input.value?.focus());
  ensureMaya().catch(() => {});
}
function brandAsk() {
  brandAskVal.value = "";
  pushMessage({ role: "assistant", special: "brandask" });
  nextTick(scrollLatest);
}
function cancelAddBrandConversation() {
  // Cancel only the new-brand flow, then restore the first monitored brand's session.
  lsRemove("soa_onb_intake");
  lsRemove("soa_onb_brandask");
  lsRemove("soa_onb_bak");
  lsRemove("soa_onb");
  lsRemove("soa_onb_b");
  lsRemove("soa_onb_k");
  lsRemove("soa_onb_bid");
  lsRemove("soa_onb_ans");
  lsRemove("soa_pending_scan");
  removeMessage(scanProgress);
  scanProgress = null;
  suspendedBrandFlow.value = null;
  scanGen.value += 1;
  intake.value = null;
  scan.value = null;
  pendingName.value = "";
  freshConversation.value = false;
  setBrandFlow(false);
  siteLookup.value = null;
  hiddenMsgs.value = [];
  messages.value = [];
  noReport.value = !brandState.items.length;
  showIntro.value = false;
  const firstBrand = brandState.items[0];
  if (firstBrand) {
    selectBrand(firstBrand.id);
    applyBrandFilter();
    void refreshMayaHistory().catch(() => {});
  }
  if (route.query.intent === "add-brand") {
    const query = { ...route.query };
    delete query.intent;
    router.replace({ path: "/maya", query }).catch(() => {});
  }
  nextTick(() => input.value?.focus());
}
async function brandAskSubmit() {
  const raw = (brandAskVal.value || "").trim();
  if (!raw) return;
  const { brand, url } = normalizeBrandAsk(raw);
  setBrandFlow(true, brand);
  removeSpecial("brandask");
  pendingName.value = brand;
  selectBrandByName(brand);
  pushMessage({ role: "user", content: raw });
  if (selectExistingBrand(brand)) return;
  intakeBegin(brand, url);
}
function onboardCard(hints) {
  hints = hints || {};
  pushMessage({
    role: "assistant", special: "onboard",
    ui: { brand: hints.brand || "", cat: hints.cat || "", url: hints.url || "", comp: hints.comp || "", sell: hints.sell || "", buyer: hints.buyer || "", region: hints.region || "", city: hints.city || "" },
  });
  nextTick(scrollLatest);
}
async function onboardCardSubmit(message) {
  const ui = message.ui || {};
  let brand = (ui.brand || "").trim();
  if (!brand) return;
  // URL syntax and SSRF policy are owned by the API; do not guess a scheme in the browser.
  let url = (ui.url || "").trim();
  const cat = (ui.cat || "").trim(), comp = (ui.comp || "").trim(), sell = (ui.sell || "").trim(), buyer = (ui.buyer || "").trim();
  messages.value.splice(messages.value.indexOf(message), 1);
  pendingName.value = brand;
  selectBrandByName(brand);
  if (selectExistingBrand(brand)) return;
  startScan(brand, url, cat, comp, sell, buyer, false, ui.region || "", ui.city || "");
}

// ══ the diagnosis scan (mayaStartScan / mayaPollScan) with the building card + recovery stash ══
function scanFailState() {
  removeMessage(scanProgress);
  scanProgress = null;
  if (!scan.value) return;
  scan.value.state = "fail";
}
function scanOkState() {
  if (!scan.value) return;
  scan.value.state = "ok";
  setTimeout(() => { if (scan.value && scan.value.state === "ok") scan.value = null; }, 1500);
}
function finishCapacityGatedBrand(brand) {
  loadBrands().then(() => {
    const existing = findBrandByName(brand);
    if (!existing) return;
    selectBrand(existing.id);
    setBrandFlow(false);
  }).catch(() => {});
}
function startScan(brand, url, cat, comp, sell, buyer, quiet, region, city) {
  freshConversation.value = true;
  suspendedBrandFlow.value = null;
  hiddenMsgs.value = [];
  messages.value = [];
  intakeClear();
  setBrandFlow(true, brand);
  pendingName.value = brand;
  selectBrandByName(brand);
  const gen = ++scanGen.value;
  lsRemove("soa_onb"); lsRemove("soa_pending_scan");
  const lastScan = { brand, url: url || "", cat: cat || "", comp: comp || "", sell: sell || "", buyer: buyer || "", region: region || "", city: city || "" };
  lsSet("soa_onb_bak", JSON.stringify(lastScan));   // a reload mid-launch rebuilds a prefilled form
  if (sell || buyer || comp) lsSet("soa_onb_ans", JSON.stringify({ sell: sell || "", buyer: buyer || "", comp: comp || "" }));
  scan.value = { state: "run", step: 0, brand, gen };
  nextTick(scrollLatest);
  if (!quiet) setTimeout(() => { if (scan.value && scan.value.state === "run" && scan.value.gen === gen) say(S.value.waitChat); }, 3000);
  if (!(url || "").trim()) {
    let told = false;
    try { told = lsGet("soa_nosite_told") === "1"; } catch { /* storage optional */ }
    if (!told) {
      lsSet("soa_nosite_told", "1");
      setTimeout(() => { if (scanGen.value === gen) say(S.value.noSite); }, 1100);
    }
  }
  onboardScan({ brand, url: url || "", cat: cat || "", comp: comp || "", sell: sell || "", buyer: buyer || "", region: region || "", city: city || "", lang: session.language })
    .then((d) => {
      if (gen !== scanGen.value) return;
      if (d && d.error === "upgrade") {
        scan.value = null;
        lsSet("soa_pending_scan", JSON.stringify(lastScan));
        lsRemove("soa_onb_bak");
        finishCapacityGatedBrand(brand);
        upgradeBubble(S.value.capFull(brand), "/account/billing");
        return;
      }
      if (!d || !d.job_id) { scanFailState(); return; }
      lsSet("soa_onb", d.job_id);
      lsSet("soa_onb_b", brand || "");
      lsSet("soa_onb_k", "onb");
      lsRemove("soa_onb_bid");
      pollScan(d.job_id, brand, gen);
    })
    .catch((error) => {
      if (gen !== scanGen.value) return;
      if (error?.status === 402 && error.payload?.error === "upgrade") {
        scan.value = null;
        lsSet("soa_pending_scan", JSON.stringify(lastScan));
        lsRemove("soa_onb_bak");
        finishCapacityGatedBrand(brand);
        upgradeBubble(S.value.capFull(brand), "/account/billing");
        return;
      }
      scanFailState();
    });
}
function pollScan(jid, brand, gen, onFail) {
  let tries = 0, unknown = 0;
  const fail = onFail || onboardRetry;
  function done() { lsRemove("soa_onb"); lsRemove("soa_onb_b"); lsRemove("soa_onb_k"); lsRemove("soa_onb_bid"); }
  const superseded = () => gen != null && scanGen.value !== gen;
  function silentlyFinishSuspended() {
    if (suspendedBrandFlow.value?.brand !== String(brand || "").trim().toLowerCase()) return false;
    suspendedBrandFlow.value = null;
    pendingName.value = "";
    loadBrands().catch(() => {});
    return true;
  }
  function tick() {
    tries += 1;
    if (superseded()) return;
    diagnosisStatus(jid).then((s) => {
      s = s || {};
      if (superseded()) return;
      if (s.status === "unknown") { unknown += 1; if (unknown > 2) { done(); scanFailState(); fail(); return; } }
      else if (s.step && scan.value) scan.value.step = s.step;
      if (s.status === "done") { done(); lsRemove("soa_onb_bak"); scanOkState(); scanDone(brand || pendingName.value || ""); return; }
      if (s.status === "error") { done(); if (silentlyFinishSuspended()) return; scanFailState(); fail(); return; }
      if (tries < SCAN_POLL_MAX) setTimeout(tick, SCAN_POLL_MS);
      else { done(); if (silentlyFinishSuspended()) return; scanFailState(); fail(); }
    }).catch(() => {
      if (superseded()) return;
      if (tries < SCAN_POLL_MAX) setTimeout(tick, SCAN_POLL_MS);
      else { done(); if (silentlyFinishSuspended()) return; scanFailState(); fail(); }
    });
  }
  setTimeout(tick, SCAN_POLL_MS);
}
function onboardRetry() {
  const ls = lsJson("soa_onb_bak") || {};
  if (ls && ls.brand) {
    const msg = say(S.value.retryLine(ls.brand));
    msg.chips = [{ label: S.value.retryBtn(ls.brand), __primary: true, retryScan: ls }];
    nextTick(scrollLatest);
    return;
  }
  say(S.value.retryBare);
  brandAsk();
}
function resumeInflightScan() {
  const jid = lsGet("soa_onb");
  if (!jid) return false;
  const brand = lsGet("soa_onb_b") || "";
  const kind = lsGet("soa_onb_k") || "";
  const bid = lsGet("soa_onb_bid") || "";
  // The current page may have just started this job. Its polling loop is already
  // active, so do not reclassify an existing-brand re-test as add-brand recovery.
  if (scan.value?.state === "run") return true;
  const rerun = kind === "rerun";
  freshConversation.value = !rerun;
  setBrandFlow(!rerun, rerun ? "" : brand);
  if (brand) {
    pendingName.value = brand;
    const brandId = Number(bid);
    if (rerun && Number.isFinite(brandId) && brandId > 0) selectBrand(brandId);
    else selectBrandByName(brand);
  }
  const gen = ++scanGen.value;
  scan.value = { state: "run", step: 0, brand, gen };
  const onFail = kind === "rerun" && bid ? () => rerunById(bid) : undefined;
  pollScan(jid, brand, gen, onFail);
  return true;
}
function rerunById(bid) {
  const id = parseInt(bid, 10);
  if (!Number.isFinite(id)) { onboardRetry(); return; }
  const progress = mayaThinking(t("maya_scan_status"));
  runBrand(id).then((started) => {
    if (started?.job_id) { scanProgress = progress; pollScan(started.job_id, pendingName.value || "", ++scanGen.value); }
    else removeMessage(progress);
  }).catch((error) => {
    removeMessage(progress);
    if (error?.status === 402) {
      upgradeBubble(error.message, error.payload?.url);
      return;
    }
    error.value = t("maya_diag_start_fail");
  });
}
function pendingOffer() {
  const o = lsJson("soa_pending_scan");
  if (!o || !o.brand || freshConversation.value) return;
  const existing = findBrandByName(o.brand);
  const isOverCapacity = existing?.evaluation_capacity_ok === false;
  const msg = say(isOverCapacity ? S.value.pendingExisting(existing.brand) : S.value.pendingLine(o.brand));
  msg.chips = isOverCapacity
    ? [{ label: S.value.pendingUpgrade, __primary: true, pendingUpgrade: existing.id }]
    : [
        { label: S.value.pendingGo(o.brand), __primary: true, pendingScan: o },
        { label: S.value.pendingNo, pendingDrop: true },
      ];
  nextTick(scrollLatest);
}
function scanDone(bn) {
  removeMessage(scanProgress);
  scanProgress = null;
  bn = bn || pendingName.value || "";
  const suspended = suspendedBrandFlow.value && suspendedBrandFlow.value.brand === String(bn).trim().toLowerCase();
  freshConversation.value = false;
  setBrandFlow(false);
  if (suspended) {
    suspendedBrandFlow.value = null;
    pendingName.value = "";
    loadBrands().catch(() => {});
    return;
  }
  if (bn) { pendingName.value = bn; selectBrandByName(bn); }
  lsRemove("soa_pending_scan");
  loadBrands().then(() => { if (bn) selectBrandByName(bn); }).catch(() => {});
  const line = S.value.doneLine(bn);
  say(line);
  logMayaMessage("assistant", line, bn).catch(() => {});
  if (session.canChat === false) { setTimeout(() => freeRead(bn), 480); return; }
  setTimeout(() => {
    waitDiagnosisCard(bn).then(() => { intakeRecap(); });
  }, 480);
}
function cardBrandTitle(card, bn) {
  if (!card || !bn) return card;
  const title = String(card.title || "");
  const low = title.toLowerCase();
  const name = String(bn).toLowerCase();
  if (low.indexOf(name) < 0) {
    const sep = title.indexOf(" · ");
    card.title = sep >= 0 ? title.slice(0, sep + 3) + bn + " · " + title.slice(sep + 3) : title + " · " + bn;
  }
  if (!card.eyeObj) card.eyeObj = "· " + bn;
  return card;
}
function showDiagnosisCard(bn) {
  return runMayaSkill({ skill: "diagnosis", lang: session.language, brand: bn || "", log: true })
    .then((result) => {
      const d = result?.obj && typeof result.obj === "object" && result.obj.ok == null && result.obj.card == null ? result : (result?.obj || result);
      if (d && d.upgrade) { freeRead(bn); return d; }
      if (d && d.ok) {
        if (d.intro) say(d.intro);
        if (d.card) { cardBrandTitle(d.card, bn); pushCard(d.card); }
        if (d.outro) setTimeout(() => say(d.outro), 600);
      }
      nextTick(scrollLatest);
      return d;
    })
    .catch(() => ({}));
}
function fallbackDiagnosisCard(bn) {
  const brand = String(bn || activeBrandForChat() || "").trim();
  const card = {
    category: "watch",
    icon: "scan",
    lang: session.language,
    kind: t("wb_kind_report"),
    title: brand ? t("maya_diag_ready_title", { b: brand }) : t("maya_diag_ready"),
    subtitle: t("maya_diag_ready_sub"),
    eye: t("wb_kind_report"),
    eyeObj: brand ? `· ${brand}` : "",
    attr: { who: "Maya", role: t("maya_role") },
    stamp: t("mc_verified"),
    acts: [{ kind: "ghost", label: t("wb_p90_report"), drawer: { type: "report", brand, sec: "overview" } }],
  };
  pushCard(card);
  return card;
}
function waitDiagnosisCard(bn, tries = 5) {
  return showDiagnosisCard(bn).then((result) => {
    if (result && (result.card || result.upgrade)) return result;
    if (tries > 0) {
      return new Promise((resolve) => {
        setTimeout(() => resolve(waitDiagnosisCard(bn, tries - 1)), 800);
      });
    }
    fallbackDiagnosisCard(bn);
    return result || {};
  });
}
function isDiagnosisCard(card) {
  return Boolean(card?.icon === "scan" && (card.acts || []).some((action) => action?.drawer?.type === "report"));
}
function repairMissingDiagnosisCard() {
  if (scan.value || lsGet("soa_onb") || messages.value.some((message) => isDiagnosisCard(cardOf(message)))) return false;
  const completed = messages.value.some((message) => {
    if (message.role !== "assistant") return false;
    return /搞定|audit.{0,100}is ready|診断.{0,100}(?:完了|でき)/i.test(String(message.content || ""));
  });
  if (!completed) return false;
  const brand = activeBrandForChat() || currentBrand.value?.brand || "";
  if (session.canChat === false) freeRead(brand);
  else waitDiagnosisCard(brand);
  return true;
}
function intakeRecap() {
  const a = lsJson("soa_onb_ans");
  lsRemove("soa_onb_ans");
  if (!a || (!a.sell && !a.buyer)) return false;
  const parts = [];
  if (a.sell) parts.push(S.value.recapSell(a.sell));
  if (a.buyer) parts.push(S.value.recapBuyer(a.buyer));
  if (a.comp) parts.push(S.value.recapComp(a.comp));
  hiddenMsgs.value.push({ role: "user", content: S.value.recapPrompt(parts) });
  mayaRun({ quietRetry: true });
  return true;
}
function freeRead(bn) {
  api("/api/brands").then((d) => {
    const bs = (d && d.brands) || [];
    let pick = null;
    for (const x of bs) {
      if (!x.latest) continue;
      if (!bn || String(x.brand).toLowerCase() === String(bn).toLowerCase()) { pick = x; if (bn) break; }
      if (!pick) pick = x;
    }
    const L = pick && pick.latest;
    if (L) {
      const score = Math.round(L.score), band = bandLabel(L.band) || "", rec = Math.round(L.rec_pct || 0);
      const comp = pick.top_comp || "";
      const compp = pick.top_comp_pct != null ? Math.round(pick.top_comp_pct) : null;
      say(S.value.freeHead(pick.brand || bn, score, band, rec, comp, compp));
    }
    freeReadFixes(pick && pick.brand || bn);
  }).catch(() => freeReadFixes(bn));
}
function freeReadFixes(bn) {
  api("/api/plan90" + (bn ? "?brand=" + encodeURIComponent(bn) : "")).then((d) => {
    let tasks = [];
    try { ((d && d.phases) || []).forEach((p) => { ((p && p.tasks) || []).forEach((task) => tasks.push(task)); }); } catch { /* plan shape varies */ }
    tasks = tasks.filter((task) => task && task.status !== "done" && task.title).slice(0, 3);
    if (tasks.length) say(S.value.freeLead + "\n" + tasks.map((task, i) => (i + 1) + ". " + task.title).join("\n"));
    freeReadCTA();
  }).catch(() => freeReadCTA());
}
function freeReadCTA() {
  upgradeBubble(S.value.freeCta, "/pricing.html");
}

// ══ the Maya agent turn (mayaRun): brand pin, hidden prompts, quiet retries ════════════════════
function agentContext(runOpts = {}) {
  const visible = messages.value.filter((m) => (m.role === "user" || m.role === "assistant") && m.content).map((m) => ({ role: m.role, content: m.content }));
  const msgs = visible.concat(hiddenMsgs.value).slice(-12).map((m) => ({ role: m.role, content: m.content }));
  if (Array.isArray(runOpts.attachments) && runOpts.attachments.length) {
    const lastUser = [...msgs].reverse().find((message) => message.role === "user" && (!runOpts.displayText || message.content === runOpts.displayText));
    if (lastUser) {
      const parts = [];
      if (runOpts.text) parts.push({ type: "text", text: runOpts.text });
      for (const attachment of runOpts.attachments) {
        if (attachment.kind === "document") parts.push({
          type: "file",
          file: {
            url: attachment.url,
            filename: attachment.filename,
            mime_type: attachment.content_type || "application/octet-stream",
          },
        });
        if (attachment.kind === "image") {
          parts.push({ type: "image_url", image_url: { url: attachment.url, detail: "auto" } });
        }
      }
      lastUser.content = parts;
    }
  }
  const pin = brandPinMessage(activeBrandForChat(), session.language);
  if (pin) msgs.unshift(pin);
  return msgs;
}
async function mayaRun(opts = {}) {
  if (sending.value) return;
  const myTurn = ++turnSeq;
  sending.value = true;
  error.value = "";
  // A send may have already painted this reply locally while an upstream skill route resolves.
  // Reuse it so the first stream token replaces the same visible response rather than adding one.
  const msg = opts.pendingReply || mayaThinking(t("maya_trace_thinking"));
  msg.streamState = "working";
  msg.streamHasOutput = false;
  msg.traceLive = true;
  if (!msg.traceStartedAt) msg.traceStartedAt = Date.now();
  const { kind, acc, action, reasoning, trace, traceMs } = await mayaStream(agentContext(opts), (shown) => {
    if (!msg.traceAnswerAt) msg.traceAnswerAt = Date.now();
    msg.streamHasOutput = true;
    msg.html = mdLite(shown, { streaming: true });
    const el = threadEl.value;
    if (el && !showLatest.value) el.scrollTop = el.scrollHeight;
  }, {
    brand: activeBrandForChat(),
    lang: session.language,
    attachments: (opts.attachments || []).map((attachment) => ({ filename: attachment.filename })),
    onReasoning: (shown) => {
      // R16b · no drawer and no scroll grab: the reasoning text is folded into the chain's one
      // `Thinking` row and stays collapsed until the customer opens it.
      msg.reasoning = shown;
    },
    onTool: (row) => applyTraceRow(msg, row),
    // The server's start-of-turn clock, translated into this browser's: the offset is what matters,
    // and comparing a server epoch against Date.now() would report whatever the two clocks disagree by.
    onStatus: (payload) => { msg.traceStartedAt = serverEpochToLocal(payload.started_at); },
  });
  if (myTurn !== turnSeq) {
    sending.value = false;
    return;
  }
  if (reasoning) msg.reasoning = reasoning;
  // The server's settled rows win over the ones the browser accumulated: they carry the past-tense
  // label, the artefact summary and the real durations. The one thing they cannot carry is WHEN
  // each call started on this browser's clock, so that stamp is carried over — it is what keeps
  // the chain the customer just watched being written in order after the turn settles.
  if (Array.isArray(trace) && trace.length) {
    const startedAt = new Map((msg.trace || []).map((row) => [row.call_id, row.startedAt]));
    msg.trace = trace.map((row) => ({ ...row, startedAt: startedAt.get(row.call_id) || row.startedAt || 0 }));
  }
  settleTrace(msg, kind);
  // The server measured the same turn with one clock and no render loop in the way; when it sent
  // its numbers they replace the ones settleTrace just estimated, so the live trace and the trace
  // this turn will replay as after a reload are the same trace.
  applyTraceMs(msg, traceMs);
  if (kind === "stopped") {
    msg.content = t("maya_stopped");
    msg.html = mdLite(msg.content);
    msg.streamState = "done";
    sending.value = false;
    persistThread();
    await nextTick();
    scrollLatest();
    input.value?.focus();
    return;
  }
  const final = (acc || "").trim();
  if (!final && opts.quietRetry) {
    // hidden prompt (post-scan recap): never surface an error — re-provision, retry once, give up quietly
    messages.value.splice(messages.value.indexOf(msg), 1);
    sending.value = false;
    try { const d = await ensureMaya(); if (d && d.ok) setTimeout(() => mayaRun({ quietFail: true, hiddenRetry: true }), 1200); } catch { /* give up quietly */ }
    return;
  }
  if (!final && opts.quietFail) {
    messages.value.splice(messages.value.indexOf(msg), 1);
    sending.value = false;
    return;
  }
  if (!final && scan.value && scan.value.state === "run") {
    msg.content = S.value.notedScan;
    msg.html = mdLite(msg.content);
    ensureMaya().catch(() => {});
  } else if (!final) {
    msg.content = t("maya_err");
    msg.html = mdLite(msg.content);
    logMayaMessage("assistant", msg.content, activeBrandForChat()).catch(() => {});
    ensureMaya().catch(() => {});
  } else {
    msg.content = final;
    msg.html = mdLite(final);
    const chips = entityChoiceChips(final);
    if (chips.length) msg.chips = chips;
    if (action) {
      msg.action = action;
      const card = action.card;
      if (card) {
        card.expanded = true;
        if (card.siteedit_token != null && card.siteedit_bid != null) registerPendingFromCard(card);
      }
    }
  }
  msg.streamState = kind === "err" ? "error" : "done";
  sending.value = false;
  persistThread();
  await nextTick();
  scrollLatest();
  input.value?.focus();
}

// ══ sending (mayaSend order): intake owns the reply → free-tier gate → skill → agent ══════════
async function send(forcedText = "", siteContext = null, opts = {}) {
  const forced = typeof forcedText === "string" ? forcedText : "";
  const draftText = typeof draft.value === "string" ? draft.value : "";
  const text = (forced || draftText).trim();
  if ((!text && !pendingAttachments.value.length) || sending.value || articleBusy.value || attachmentBusy.value) return;
  const selectedAttachments = pendingAttachments.value.map((item) => item.file);
  const displayText = text || t("maya_attachment_prompt");
  noReport.value = false; showIntro.value = false;
  const userMessage = pushMessage({
    role: "user",
    content: displayText,
    attachments: selectedAttachments.map((file) => file.name),
  });
  // Show Maya's response state before uploads, skill routing, or a stream connection reaches the server.
  const pendingReply = mayaThinking(t("maya_thinking"));
  persistThread();
  draft.value = "";
  pendingAttachments.value = [];
  attachmentError.value = "";
  await nextTick();
  growInput();
  if (selectedAttachments.length) {
    attachmentBusy.value = true;
    try {
      const uploaded = await Promise.all(selectedAttachments.map((file) => uploadMayaAttachment(file)));
      await mayaRun({ attachments: uploaded, text: displayText, displayText, pendingReply });
    } catch (caught) {
      const index = messages.value.indexOf(userMessage);
      if (index >= 0) messages.value.splice(index, 1);
      removeMessage(pendingReply);
      pendingAttachments.value = selectedAttachments.map((file) => ({ id: ++attachmentId, file }));
      attachmentError.value = caught?.message || t("maya_attachment_failed");
    } finally {
      attachmentBusy.value = false;
    }
    return;
  }
  if (article.tryTypedRetry(text)) { removeMessage(pendingReply); scrollLatest(); return; }
  if (intake.value && !forcedText && ["q1", "q2", "q3"].includes(intake.value.stage)) { removeMessage(pendingReply); scrollLatest(); intakeAnswer(text); return; }
  if (intake.value && intake.value.stage === "confirm" && (isIntakeCancel(text) || wantsNewScan(text))) { removeMessage(pendingReply); scrollLatest(); intakeConfirmCancel(); return; }
  if (session.canChat === false) {
    removeMessage(pendingReply);
    upgradeBubble(S.value.chatGate, "/pricing.html");
    scrollLatest();
    input.value?.focus();
    return;
  }
  const offered = lastOfferedEntities();
  if (offered.length) {
    const prompt = [...messages.value].reverse().find((message) => message.role === "assistant" && message.chips?.some((chip) => chip.entity));
    if (prompt) prompt.chips = null;
    // Entity choices are conversational disambiguation, not a brand rename. Keep the
    // original user answer in the Hermes context, regardless of its exact wording, so it
    // can distinguish the monitored brand (for example Chrome) from a platform mentioned
    // in the answer (Google Search) without a keyword route.
    await mayaRun({ pendingReply });
    return;
  }
  // MAYA._forceAgent, ported: a card question goes straight to the free-chat agent, skipping the
  // deterministic skill router (the same order the pre-Vue mayaTrySkill used for forced asks).
  if (opts.agent) { await mayaRun({ pendingReply }); return; }
  // Free chat remains server-owned. Add-brand onboarding is entered by explicit UI actions, while
  // wantsNewScan is reserved for cancelling an already-open confirmation card above.
  sending.value = true;
  const activeBrand = activeBrandForChat();
  const faqItem = pendingFaqItem;
  pendingFaqItem = null;
  skillAbort = new AbortController();
  const myTurn = turnSeq;
  try {
    const result = await runMayaSkill({ text, lang: session.language, log: true, brand: opts.brand || activeBrand, ...(opts.skill ? { skill: opts.skill } : {}), ...(opts.buildPrep && typeof opts.buildPrep === "object" ? { build_prep: opts.buildPrep } : {}), ...(siteContext && typeof siteContext === "object" ? { site_context: siteContext } : {}), ...(faqItem ? { faq_item: faqItem } : {}) }, { signal: skillAbort.signal });
    if (myTurn !== turnSeq) return;
    if (result.state === "running" && result.job) { saveJob(result.job); await resumeJob(result.job, pendingReply); }
    else {
      const payload = result?.obj && typeof result.obj === "object" && result.obj.ok == null && result.obj.card == null ? result : (result?.obj || result);
      if (payload && payload.upgrade) { handleSkillResult(payload, pendingReply); }
      else if (payload && payload.ok && payload.card) { handleSkillResult(payload, pendingReply); }
      else { sending.value = false; await mayaRun({ pendingReply }); return; }   // no skill matched → the agent
    }
  } catch (caught) {
    if (caught.name === "AbortError" || myTurn !== turnSeq) { sending.value = false; return; }
    sending.value = false; await mayaRun({ pendingReply }); return;   // skill request failed → the agent
  } finally {
    skillAbort = null;
    if (sending.value) sending.value = false;
    persistThread();
    await nextTick();
    scrollLatest();
    input.value?.focus();
  }
}

// robustly deliver an entry ask (?ask / handoffs): send once the thread has settled (mcAskPump)
function pumpAsk(q, skill, n = 0) {
  if (!q) return;
  if (!loading.value && !sending.value) {
    nextTick(() => { send(q, null, skill ? { skill } : {}); });
    return;
  }
  if (n < 40) setTimeout(() => pumpAsk(q, skill, n + 1), 180);
}

// ══ entry (mayaInit): handoffs first, history per brand, then intro/onboard/openResult ═════════
function consumeJson(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    sessionStorage.removeItem(key);
    return JSON.parse(raw);
  } catch { return null; }
}
function consumeLandingHandoff() {
  let brand = "", url = "", cat = "", comp = "";
  try {
    const pending = JSON.parse(localStorage.getItem("soa_p") || localStorage.getItem("soa_pending") || "null");
    if (pending && pending.brand) {
      localStorage.removeItem("soa_p");
      localStorage.removeItem("soa_pending");
      sessionStorage.setItem("soa_onb_brand", pending.brand);
      if (pending.url) sessionStorage.setItem("soa_onb_url", pending.url);
      else sessionStorage.removeItem("soa_onb_url");
      cat = pending.cat || "";
      comp = pending.comp || "";
    }
    brand = (sessionStorage.getItem("soa_onb_brand") || "").trim();
    url = (sessionStorage.getItem("soa_onb_url") || "").trim();
    sessionStorage.removeItem("soa_onb_brand");
    sessionStorage.removeItem("soa_onb_url");
  } catch { /* storage optional */ }
  return brand ? { brand, url, cat, comp } : null;
}
async function onboardEntry(handoff) {
  noReport.value = false; showIntro.value = false;
  if (resumeInflightScan()) {
    say(S.value.stillFinishing);
    return;
  }
  let intk = lsJson("soa_onb_intake");
  // a fresh landing handoff is EXPLICIT intent: it overrides a stale mid-calibration for a DIFFERENT brand
  if (intk && intk.brand && handoff && String(intk.brand).toLowerCase() !== String(handoff.brand).toLowerCase()) { lsRemove("soa_onb_intake"); intk = null; }
  if (handoff && selectExistingBrand(handoff.brand)) return;
  if (intk && intk.brand && intk.stage) { intakeResume(intk); return; }
  if (handoff) {
    intakeBegin(handoff.brand, handoff.url, handoff.cat, handoff.comp);
    return;
  }
  const bak = lsJson("soa_onb_bak");
  if (bak && bak.brand) {
    say(S.value.interrupted);
    onboardCard(bak);
    return;
  }
  const dom = guessDomain(session.user?.email);
  if (dom) { intakeBegin(brandFromDomain(dom), "https://" + dom); return; }
  say(S.value.onboardHi);
  onboardCard();
}
function openResult() {
  if (session.canChat === false) { freeRead(currentBrand.value?.brand || ""); return; }
  showDiagnosisCard(currentBrand.value?.brand || "");
}
function meetMaya() {
  showIntro.value = false;
  ensureMaya().then((d) => {
    if (d && d.reason === "no_report") { onboardEntry(consumeLandingHandoff()); return; }
    hiddenMsgs.value.push({ role: "user", content: S.value.greetPrompt() });
    mayaRun({});
  }).catch(() => {
    hiddenMsgs.value.push({ role: "user", content: S.value.greetPrompt() });
    mayaRun({});
  });
}

async function load() {
  loading.value = true; error.value = ""; noReport.value = false; showIntro.value = false;
  try {
    await loadBrands().catch(() => {});
    void refreshSites();
    const handoff = consumeLandingHandoff();
    const alertHandoff = consumeJson("soa_alert_handoff");
    const reportHandoff = consumeJson("soa_report_handoff");
    const retestBrandId = Number(route.query.retest_brand);
    if (Number.isFinite(retestBrandId) && retestBrandId > 0) {
      selectBrand(retestBrandId);
      // A dashboard retest is an existing brand flow. Clear any stale add-brand state
      // left behind when navigating from Maya without remounting the SPA shell.
      setBrandFlow(false);
      pendingName.value = "";
      freshConversation.value = false;
      scan.value = null;
      suspendedBrandFlow.value = null;
      intakeClear();
      const query = { ...route.query };
      delete query.retest_brand;
      router.replace({ query }).catch(() => {});
    }
    const addingBrand = route.query.intent === "add-brand";
    let ask = String(route.query.ask || "").trim();
    const askBrand = String(route.query.brand || "").trim();
    let promptQ = String(route.query.prompt || "").trim();
    const promptSkill = String(route.query.skill || "").trim();
    if (ask) {
      const query = { ...route.query };
      delete query.ask;
      router.replace({ query }).catch(() => {});   // report ?ask is consumed once (mcIncomingAsk stripped it too)
    }

    if (addingBrand) { startAddBrandConversation(); return; }

    if (ask && askBrand) selectBrandByName(askBrand);
    else if (reportHandoff && reportHandoff.brand) selectBrandByName(reportHandoff.brand);
    else if (alertHandoff && alertHandoff.severity !== "maya" && alertHandoff.brand) selectBrandByName(alertHandoff.brand);

    const data = await getMayaHistory();
    histRaw.value = data.msgs || [];
    applyBrandFilter();
    restoreLocalThread();
    const deliverableSlug = String(route.query.deliverable || "").trim();
    if (!wbQueryOpened && deliverableSlug) {
      wbQueryOpened = true;
      openWorkbenchFromPayload({ type: "deliverable", slug: deliverableSlug });
      const query = { ...route.query };
      delete query.deliverable;
      router.replace({ query }).catch(() => {});
    }
    const taskId = Number(route.query.task);
    if (!taskQueryOpened && Number.isFinite(taskId) && taskId > 0) {
      taskQueryOpened = true;
      openWorkbenchFromPayload({ type: "task", id: taskId });
    }
    if (messages.value.length) {
      if (handoff) {
        const h = handoff;
        if (!selectExistingBrand(h.brand)) {
          intakeBegin(h.brand, h.url, h.cat, h.comp);   // returning user submitted a new brand on the homepage → calibrate it in the restored thread
        }
      } else {
        setTimeout(() => {
          const resumed = resumeInflightScan();
          if (!resumed) repairMissingDiagnosisCard();
          pendingOffer();
        }, 900);
        const job = savedJob();
        if (job) await resumeJob(job);
        void startArticlePlan();
        for (const message of messages.value) {
          const card = cardOf(message);
          if (card && card.siteedit_token != null && card.siteedit_bid != null && !card._seState) registerPendingFromCard(card);
        }
      }
      if (Number.isFinite(retestBrandId) && retestBrandId > 0 && currentBrand.value) {
        await rerunDiagnosis(currentBrand.value.brand);
        return;
      }
      const wbParam = Number(route.query.wb);
      if (!wbQueryOpened && Number.isFinite(wbParam) && wbParam > 0) { wbQueryOpened = true; openSiteWorkbench(wbParam); }
      settleToBottom();
      if (!(route.query.article_q || route.query.article_edit)) dispatchEntryAsk({ ask, promptQ, promptSkill, alertHandoff, reportHandoff });
      return;
    }

    void startArticlePlan();
    if (route.query.article_q || route.query.article_edit || article.stage.value) {
      settleToBottom();
      return;
    }

    const ready = await ensureMaya();
    if (ask || promptQ || alertHandoff || reportHandoff) {
      // arriving with a question — the chat opens and the pump sends it; no intro/onboarding competes
      settleToBottom();
      dispatchEntryAsk({ ask, promptQ, promptSkill, alertHandoff, reportHandoff });
      return;
    }
    if (ready.reason === "no_report") { onboardEntry(handoff); return; }
    if (ready.ok) {
      if (handoff) {
        if (!selectExistingBrand(handoff.brand)) intakeBegin(handoff.brand, handoff.url, handoff.cat, handoff.comp);
        return;
      }   // has a report but arrived with a new brand → calibrate it
      if (Number.isFinite(retestBrandId) && retestBrandId > 0 && currentBrand.value) {
        await rerunDiagnosis(currentBrand.value.brand);
        return;
      }
      openResult();
      return;
    }
    showIntro.value = true;
  } catch (caught) {
    if (caught.name !== "AbortError") error.value = caught.message || t("maya_open_fail");
  } finally {
    loading.value = false;
    await nextTick();
    settleToBottom();
    input.value?.focus();
  }
}
function dispatchEntryAsk({ ask, promptQ, promptSkill, alertHandoff, reportHandoff }) {
  if (ask) { pumpAsk(ask); return; }
  if (alertHandoff && alertHandoff.severity !== "maya") {
    const message = String(alertHandoff.message || "").slice(0, 140);
    const skill = AKIND_SKILL[alertHandoff.kind] || null;
    pumpAsk(S.value.alertAsk(alertHandoff.brand || "", message), skill);
    return;
  }
  if (reportHandoff && reportHandoff.brand) { pumpAsk(S.value.aboutBrand(reportHandoff.brand)); return; }
  // insight chips auto-send (askMayaAbout → mcAsk), not a prefill; once per session per question
  if (promptQ) {
    const guard = "soa_prompt_sent:" + promptQ;
    try {
      if (sessionStorage.getItem(guard)) return;
      sessionStorage.setItem(guard, "1");
    } catch { /* storage optional */ }
    pumpAsk(promptQ, promptSkill);
  }
}

function growInput() { const el = input.value; if (!el) return; el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 160)}px`; }
function inputKeydown(event) { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); send(); } }
function openAttachmentPicker() { attachmentInput.value?.click(); }
function onAttachmentChange(event) {
  attachmentError.value = "";
  const files = [...(event.target.files || [])];
  pendingAttachments.value.push(...files.map((file) => ({ id: ++attachmentId, file })));
  event.target.value = "";
}
function removeAttachment(id) { pendingAttachments.value = pendingAttachments.value.filter((item) => item.id !== id); }
function formatAttachmentSize(bytes) {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}
function scrollLatest() { const el = threadEl.value; if (!el) return; el.scrollTo({ top: el.scrollHeight, behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" }); showLatest.value = false; }
function openPalette(event) {
  paletteTrigger = event.currentTarget;
  paletteOpen.value = true;
  void refreshSites();
  nextTick(() => paletteEl.value?.querySelector(".mc-skill")?.focus());
}
function closePalette(restoreFocus = true) {
  paletteOpen.value = false;
  if (!restoreFocus) return;
  nextTick(() => {
    if (paletteTrigger?.isConnected) paletteTrigger.focus();
    paletteTrigger = null;
  });
}
function paletteKeydown(event) {
  if (event.key === "Escape") { event.preventDefault(); closePalette(); return; }
  if (event.key !== "Tab") return;
  const focusable = [...event.currentTarget.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])')]
    .filter((element) => element.getAttribute("aria-hidden") !== "true");
  if (!focusable.length) return;
  const first = focusable[0], last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
}
async function refreshSites() {
  const data = await listSites();
  sitesByBrandId.value = Object.fromEntries(
    (data?.sites || []).filter((site) => site?.brand_id != null).map((site) => [String(site.brand_id), site]),
  );
  return sitesByBrandId.value;
}
async function useSkill(skill, event) {
  const trigger = event?.currentTarget;
  closePalette(false);
  // F-402: the paywall arrives instead of the work, not after it — no page is generated, no
  // cluster is picked, and the sentence names the plan and what it buys.
  if (skill.locked) { upgradeBubble(t("maya_upgrade"), "/account/billing"); return; }
  if (skill.action === "attach") { openAttachmentPicker(); return; }
  if (skill.action === "add") { startAddBrandConversation(); return; }
  if (skill.action === "build") {
    const brand = currentBrand.value?.brand || "";
    const brandId = currentBrand.value?.id;
    if (!brand) { startAddBrandConversation(); return; }
    try {
      const sites = await refreshSites();
      const site = sites[String(brandId)];
      if (site && ["draft", "published"].includes(site.status)) { openSiteWorkbench(site.brand_id, trigger); return; }
    } catch (caught) {
      soaToast(caught?.message || t("maya_open_fail"), "", "warn");
      nextTick(() => paletteTrigger?.isConnected ? paletteTrigger.focus() : input.value?.focus());
      return;
    }
    if (brand) openBuildPrep(brand);
    else startAddBrandConversation();
    return;
  }
  if (skill.action === "card") {
    askSentence(skill.text, false, { skill: skill.skill });
    return;
  }
  askSentence(skill.text, false);
}
// ── workbench drawer (ported wbSiteStudio): right-side surfaces keep Maya in a left column ──
function openSiteWorkbench(brandId, trigger) {
  if (brandId == null) return;
  if (trigger?.currentTarget) workbenchTrigger = trigger.currentTarget;
  else if (trigger?.focus) workbenchTrigger = trigger;
  workbench.value = { type: "sitewb", brand_id: Number(brandId), wide: true };
}
// wbFromPayload, ported: every drawer payload the server sends lands on the right surface.
// `article` is the legacy alias of `deliverable`; fetch-backed drawers start on the loading title
// exactly like the old wbOpen, and a narrate line is spoken into the chat, never written to history.
function openWorkbenchFromPayload(payload, trigger) {
  if (typeof payload === "string") {
    try { payload = JSON.parse(payload); } catch { return; }
  }
  if (!payload || typeof payload !== "object") return;
  const wb = { ...payload };
  if (wb.type === "article") wb.type = "deliverable";
  if (wb.type === "sitewb" && wb.brand_id != null) { openSiteWorkbench(wb.brand_id, trigger); return; }
  if (trigger?.currentTarget) workbenchTrigger = trigger.currentTarget;
  else if (trigger?.focus) workbenchTrigger = trigger;
  if (!wb.title && ["deliverable", "site", "report", "plan90", "task"].includes(wb.type)) wb.title = t("wb_loading");
  workbench.value = wb;
  if (wb.narrate) say(wb.narrate);
}
function onWbMeta(meta) {
  if (!workbench.value || !meta) return;
  workbench.value = { ...workbench.value, ...meta };
}
function onWbBubble(bubble) {
  if (!bubble?.content) return;
  pushMessage({ role: bubble.role === "user" ? "user" : "assistant", content: bubble.content, html: mdLite(bubble.content), action: bubble.action || null });
}
// mcAsk, ported: a sentence handed to her from a button. When she's mid-turn it parks in the
// composer with a spoken reason instead of vanishing (the pre-Vue "dead click" rule).
function askSentence(sentence, forceAgent, options = {}) {
  if (!sentence) return;
  if (sending.value || articleBusy.value) {
    draft.value = sentence;
    nextTick(() => { growInput(); input.value?.focus(); });
    soaToast(t("mc_busy"), "", "warn");
    return;
  }
  send(sentence, null, forceAgent ? { agent: true } : options);
}
function closeWorkbench() {
  const restore = workbenchTrigger;
  workbenchTrigger = null;
  workbench.value = null;
  nextTick(() => {
    if (restore?.isConnected) restore.focus();
    else input.value?.focus();
  });
}

function upgradeBubble(msg, url) {
  const destination = url || "/pricing.html";
  const action = destination === "/account/billing"
    ? { kind: "cta", label: t("maya_see_plans"), view: "account/billing" }
    : { kind: "cta", label: t("maya_see_plans"), url: destination };
  pushMessage({ role: "assistant", content: msg || t("maya_upgrade"), action: { card: { kind: "Upgrade", title: t("maya_upgrade_title"), subtitle: "", expanded: true, acts: [action] } } });
}
function pushCard(card) { if (card) card.expanded = true; pushMessage({ role: "assistant", content: "", action: { card } }); }   // a pushed card is answering this turn → opens itself
function workbenchAsk(sentence, siteContext) {
  noReport.value = false;
  showIntro.value = false;
  send(sentence, siteContext);
}
function newBlogCard(brand) {
  const progress = mayaThinking(t("maya_blog_prep"));
  runMayaSkill({ skill: "content", lang: session.language, brand: brand || "" }).then((result) => {
    const payload = result?.obj || result;
    if (payload?.upgrade) { upgradeBubble(payload.msg, payload.url); return; }
    handleSkillResult(payload);
  }).catch((err) => { error.value = err.message || t("maya_art_start_fail"); })
    .finally(() => removeMessage(progress));
}
function openBuildPrep(brand) {
  const name = String(brand || currentBrand.value?.brand || "").trim();
  if (name) selectBrandByName(name);
  if (buildPrep.value?.brand === name) { nextTick(scrollLatest); return; }
  buildPrep.value = { brand: name };
  nextTick(scrollLatest);
}
function startPreparedBuild(prep) {
  const brand = buildPrep.value?.brand || currentBrand.value?.brand || "";
  buildPrep.value = null;
  const command = session.language === "zh" ? "帮我建站" : session.language === "ja" ? "サイトを作って" : "Build a website";
  send(command, null, { skill: "sitebuild", buildPrep: prep && typeof prep === "object" ? prep : null, brand });
}
function mountSiteWorkbenchPanel(el, brandId) {
  wsHandle = mountSiteWorkbench(el, brandId, {
    api,
    t,
    lang: () => session.language,
    prefix: apiPrefix,
    toast: soaToast,
    upgrade: (msg, url) => upgradeBubble(msg, url),
    copy: (txt) => { try { navigator.clipboard?.writeText(txt); } catch { /* optional */ } },
    ask: workbenchAsk,
    // Composer is pinned at the bottom of the Maya column. Centering it (or
    // letting focus() scroll) moves .main (overflow:hidden) and lifts the input.
    focusChat: () => { try { input.value?.focus({ preventScroll: true }); } catch { /* detached */ } },
    newBlog: newBlogCard,
    openDeliverable: () => router.push("/files"),
    pushCard,
    selectBrandByName: (name) => {
      if (!name) return;
      loadBrands().then(() => { selectBrandByName(name); }).catch(() => {});
    },
    onPendingResolved: (token, state) => resolveSiteEditCards(token, state),
  });
  if (pendingSiteEdit) wsHandle.setPending(pendingSiteEdit);
}
watch(() => [workbench.value?.type, workbench.value?.brand_id], async ([type, brandId], [, prevBrandId]) => {
  // Every workbench surface opens beside Maya. The site canvas is wider, but task/report/
  // deliverable drawers use the same left-column reflow so the chat never sits underneath them.
  shell.wbTwoCol = Boolean(type);
  if (type === "sitewb") {
    if (wsHandle && String(prevBrandId) !== String(brandId)) { wsHandle.destroy(); wsHandle = null; }
    if (!wsHandle) {
      await nextTick();
      if (wsRoot.value && Number(brandId) === Number(workbench.value?.brand_id)) mountSiteWorkbenchPanel(wsRoot.value, brandId);
    }
  } else if (wsHandle) {
    wsHandle.destroy();
    wsHandle = null;
  }
});
function handleWorkbenchKeys(event) {
  if (event.key !== "Escape") return;
  if (paletteOpen.value) { event.preventDefault(); closePalette(); return; }
  if (workbenchOpen.value) { event.stopPropagation(); closeWorkbench(); return; }
  if (sending.value) { event.preventDefault(); stopMaya(); }
}

// ── staged site edits: the chat card applies/discards in place, the workbench canvas previews ──
function resolveSiteEditCards(token, state) {
  for (const message of messages.value) {
    const card = cardOf(message);
    if (card && card.siteedit_token && String(card.siteedit_token) === String(token)) card._seState = state;
  }
}
async function registerPendingFromCard(card) {
  if (card?.siteedit_token == null || card.siteedit_bid == null) return;
  try {
    const j = await getPendingSiteEdit(card.siteedit_bid);
    const alive = j?.pending && j.token && String(j.token) === String(card.siteedit_token);
    if (!alive) { card._seState = "gone"; clearWorkbenchPending(card.siteedit_bid, card.siteedit_token); return; }
    pendingSiteEdit = { brand_id: card.siteedit_bid, token: j.token, blocks: j.preview || card.siteedit_preview || [] };
    if (wsHandle) wsHandle.setPending(pendingSiteEdit);
  } catch { /* pending check is best-effort */ }
}
function clearWorkbenchPending(bid, token, keepCanvas) {
  if (!pendingSiteEdit) return;
  if (bid != null && String(pendingSiteEdit.brand_id) !== String(bid)) return;
  if (token && String(pendingSiteEdit.token) !== String(token)) return;
  pendingSiteEdit = null;
  if (wsHandle) wsHandle.clearPending(bid, token, keepCanvas);
}
async function performSiteEdit(cfg) {
  const key = siteEditBusyKey(cfg);
  if (!cfg?.action || !key || cardActionBusy[key]) return;
  cardActionBusy[key] = true;
  try {
    const j = cfg.action === "apply" ? await applySiteEdit(cfg.brand_id, cfg.token) : await discardSiteEdit(cfg.brand_id, cfg.token);
    if (j && (j.upgrade || j.error === "upgrade")) { upgradeBubble(j.msg, j.url); return; }
    if (cfg.action === "discard") {
      if (j && (j.ok || j.gone)) { resolveSiteEditCards(cfg.token, "discarded"); clearWorkbenchPending(cfg.brand_id, cfg.token); }
      else soaToast(t("se_fail"), "", "warn");
      return;
    }
    if (j && j.ok && j.applied) {
      resolveSiteEditCards(cfg.token, "applied");
      soaToast(t("se_applied"), "", "ok");
      clearWorkbenchPending(cfg.brand_id, cfg.token, true);
      if (wsHandle) wsHandle.syncDraftMeta();
      return;
    }
    if (j && (j.drift || j.stale)) { resolveSiteEditCards(cfg.token, "stale"); clearWorkbenchPending(cfg.brand_id, cfg.token); soaToast(t("se_stale"), "", "warn"); return; }
    if (j && j.gone) { resolveSiteEditCards(cfg.token, "gone"); clearWorkbenchPending(cfg.brand_id, cfg.token); return; }
    soaToast(t("se_fail"), "", "warn");
  } catch { soaToast(t("se_fail"), "", "warn"); }
  finally { delete cardActionBusy[key]; }
}
function bandName(band) {
  return bandLabel(band) || "—";
}
function openCurrentReport() {
  if (rbarBrand.value?.id) router.push({ path: "/reports", query: { brand_id: rbarBrand.value.id } });
  else router.push({ path: "/reports" });
}
function runCurrentBrandDiagnosis() {
  if (rbarBrand.value?.id) rerunDiagnosis(rbarBrand.value.brand).catch((err) => { error.value = err.message || t("maya_diag_start_fail"); });
  else startAddBrandConversation();
}
function syncLatestButton() {
  const el = threadEl.value;
  if (!el) { showLatest.value = false; return; }
  showLatest.value = el.scrollHeight - el.scrollTop - el.clientHeight > 80;
}
function settleToBottom() {
  requestAnimationFrame(() => requestAnimationFrame(() => {
    const el = threadEl.value;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    syncLatestButton();
  }));
}

function handleSkillResult(result, reply = null) {
  const normalized = skillPayload(result);
  const payload = normalized?.obj && typeof normalized.obj === "object" && normalized.obj.ok == null && normalized.obj.card == null ? normalized : (normalized?.obj || normalized);
  if (!payload) return;
  if (payload.state === "failed") throw new Error(payload.reason || t("maya_task_fail"));
  const message = reply || pushMessage({ role: "assistant", content: "" });
  if (payload.upgrade) {
    message.content = payload.msg || t("maya_upgrade");
    message.html = mdLite(message.content);
    message.action = { card: { kind: "Upgrade", title: t("maya_upgrade_title"), subtitle: payload.need || "", expanded: true, acts: [{ kind: "cta", label: t("maya_see_plans"), url: payload.url || "/pricing.html" }] } };
    message.streamState = "done";
    message.streamHasOutput = false;
    message.traceLive = false;  // R16b
    return;
  }
  // a card answering THIS turn opens itself (the pre-Vue renderMayaCard `live` toggle); history
  // replay stays folded — only the client copy is expanded, the logged one keeps its folded state
  if (payload.card) payload.card.expanded = true;
  message.content = payload.intro || payload.msg || payload.content || payload.card?.title || t("maya_done");
  message.html = mdLite(message.content);
  message.action = payload.card ? { card: payload.card } : null;
  message.streamState = "done";
  message.streamHasOutput = false;
  message.traceLive = false;  // R16b
  if (payload.outro) setTimeout(() => say(payload.outro), 600);
  // workbench side-channels (same as the pre-Vue app): a staged site edit previews on the canvas,
  // a page change repaints the drawer, a hero regen lands late and needs one refresh.
  if (payload.card?.siteedit_token != null && payload.card.siteedit_bid != null) registerPendingFromCard(payload.card);
  if (payload.wsrefresh !== undefined && wsHandle) {
    const slug = payload.wsrefresh || "";
    wsHandle.reloadDraft(false, slug || wsHandle.page());
  }
  if (payload.reimage && wsHandle) wsHandle.reimageSoon();
}
function copyText(text) { if (!text) return; navigator.clipboard?.writeText(String(text)).then(() => say(t("maya_copied"))).catch(() => say(t("maya_copy_fail"))); }
function stopMaya() {
  turnSeq += 1;
  stopMayaStream();
  try { skillAbort?.abort(); } catch { /* already closed */ }
  polling.stop?.();
  const working = [...messages.value].reverse().find((message) => message.streamState === "working" || message.traceLive);
  // stopMaya bumps turnSeq above, so mayaRun early-returns and never reaches its own
  // settleTrace — without this the rows from the stopped turn spin forever.
  if (working) settleTrace(working, "stopped");
  if (working && !working.content) {
    working.content = t("maya_stopped");
    working.html = mdLite(working.content);
    working.streamState = "done";
  } else if (!messages.value.some((message) => message.content === t("maya_stopped"))) {
    say(t("maya_stopped"));
  }
  sending.value = false;
  persistThread();
}
async function rerunDiagnosis(brandName) {
  const key = String(brandName || "").trim().toLowerCase();
  const selected = currentBrand.value;
  const brand = selected && String(selected.brand || "").trim().toLowerCase() === key ? selected : null;
  if (!brand?.id) { send(t("maya_diag_rerun_q", { b: brandName || t("this_brand") })); return; }
  const progress = mayaThinking(t("maya_scan_status"));
  let started;
  try { started = await runBrand(brand.id); }
  catch (error) {
    removeMessage(progress);
    if (error?.status === 402) {
      upgradeBubble(error.message, error.payload?.url);
      return;
    }
    throw error;
  }
  if (!started?.job_id) { removeMessage(progress); return; }
  scanProgress = progress;
  say(t("maya_diag_rerun", { b: brand.brand }));
  if (started?.job_id) {
    freshConversation.value = false;
    pendingName.value = brand.brand;
    scan.value = { state: "run", step: 0, brand: brand.brand, gen: ++scanGen.value };
    lsSet("soa_onb", started.job_id);
    lsSet("soa_onb_b", brand.brand);
    lsSet("soa_onb_k", "rerun");
    lsSet("soa_onb_bid", String(brand.id));
    pollScan(started.job_id, brand.brand, scanGen.value);
  }
}
function resolvePreviousCards(brandId, state) {
  for (const message of messages.value) {
    const card = cardOf(message);
    if (!card) continue;
    const matches = (card.acts || []).some((action) => action.prev && String(action.prev.brand_id) === String(brandId));
    if (matches) card._prevResolved = state || "gone";
  }
}
function siteEditBusyKey(cfg) {
  return cfg?.brand_id != null && cfg.token ? `siteedit:${cfg.brand_id}:${cfg.token}` : "";
}
function previousSiteBusyKey(prev) {
  return prev?.brand_id != null && prev.choice ? `prev:${prev.brand_id}` : "";
}
function cardActionBusyKey(action) {
  return siteEditBusyKey(action?.siteedit) || previousSiteBusyKey(action?.prev);
}
function isCardActionBusy(action) {
  const key = cardActionBusyKey(action);
  return Boolean(key && cardActionBusy[key]);
}
async function choosePreviousSite(prev) {
  const key = previousSiteBusyKey(prev);
  if (!key || cardActionBusy[key]) return;
  cardActionBusy[key] = true;
  try {
    const result = await api(`/api/site/prev/${encodeURIComponent(prev.choice)}`, { method: "POST", json: { brand_id: prev.brand_id } });
    const state = result?.choice || prev.choice || "gone";
    resolvePreviousCards(prev.brand_id, state);
    if (state === "reset" && workbench.value?.type === "sitewb" && String(workbench.value.brand_id) === String(prev.brand_id)) closeWorkbench();
    if (result?.msg) soaToast(result.msg, "", "ok");
    void loadBrands();
  } catch (caught) { error.value = caught?.message || t("maya_site_choice_fail"); }
  finally { delete cardActionBusy[key]; }
}
function outreachHtml(parsed, raw) {
  if (!parsed?.ok) return mdLite(raw || "");
  const subject = parsed.subject || parsed.title || "";
  const safeSubject = subject.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]);
  const extra = [];
  if (parsed.why) extra.push(`<div class="ob-whyp">${mdLite(parsed.why)}</div>`);
  if (parsed.followup) extra.push(`<div class="ob-whyp"><b>${t("ob_followup")}: </b>${mdLite(parsed.followup)}</div>`);
  return `${subject ? `<div class="ob-subj"><b>${safeSubject}</b></div>` : ""}<div class="ob-msg">${mdLite(parsed.message)}</div>${extra.length ? `<details class="ob-why"><summary>${t("ob_why")}</summary>${extra.join("")}</details>` : ""}`;
}
async function draftMessageOutreach(message) {
  const action = message.action || {};
  if (!action.domain || action.brand_id == null || outreachPanels[message.id]?.state === "loading") return;
  outreachPanels[message.id] = { state: "loading" };
  try {
    const result = await draftBrandOutreach(action.brand_id, { domain: action.domain, kind: action.kind === "cited" ? "cited" : "opportunity" });
    if (!result?.ok || !result.content) { outreachPanels[message.id] = { state: "error", text: result?.limit ? t("ob_limit") : t("ob_err") }; return; }
    const parsed = obParse(result.content);
    outreachPanels[message.id] = { state: "done", cached: !!result.cached, raw: result.content, parsed, html: outreachHtml(parsed, result.content), mailto: obMailtoStr(parsed), copied: false };
  } catch { outreachPanels[message.id] = { state: "error", text: t("ob_err") }; }
}
function copyOutreach(message) {
  const panel = outreachPanels[message.id];
  if (!panel || panel.state !== "done") return;
  navigator.clipboard?.writeText(obCopyStr(panel.parsed, panel.raw)).then(() => {
    panel.copied = true;
    setTimeout(() => { if (outreachPanels[message.id]) outreachPanels[message.id].copied = false; }, 1600);
  }).catch(() => {});
}
function runCardAction(act, event) {
  // pre-Vue rule: a card url ALWAYS opened a new tab — the report lives at /reports/<slug>.html,
  // a static page outside the SPA, so routing it in-app would dead-end on the reports list view.
  if (act.url) {
    const url = String(act.url);
    if (url) window.open(url, "_blank", "noopener");
    return;
  }
  if (act.view) { router.push({ path: `/${act.view}` }); return; }
  if (act.drawer) { openWorkbenchFromPayload(act.drawer, event); return; }
  if (act.copy) { copyText(act.copy); return; }
  if (act.siteedit) { performSiteEdit(act.siteedit); return; }
  if (act.build != null) { openBuildPrep(act.build); return; }
  if (act.rerun) { rerunDiagnosis(act.rerun).catch((err) => { error.value = err.message || t("maya_diag_start_fail"); }); return; }
  // `say` rides deterministic skill routing; `q` forces the free-chat agent (mcAsk's forceAgent)
  if (act.say) { askSentence(act.say, false); return; }
  if (act.q) { askSentence(act.q, true); return; }
  if (act.prev?.choice) { void choosePreviousSite(act.prev); }
}
// R16b · an artefact chip with a destination is a door; one without is a label and never fires.
function openTraceChip(chip) { if (chip?.view) router.push({ path: `/${chip.view}` }); }
function runChipAction(message, chip, index, event) {
  const action = message.action;
  if (chip.retryArticle) { message.chips = null; message.action = null; article.runRetry(chip); return; }
  if (chip.retryScan) { message.chips = null; message.action = null; startScan(chip.retryScan.brand, chip.retryScan.url, chip.retryScan.cat, chip.retryScan.comp, chip.retryScan.sell, chip.retryScan.buyer, false, chip.retryScan.region || "", chip.retryScan.city || ""); return; }
  if (chip.pendingUpgrade) {
    message.chips = null;
    message.action = null;
    const brand = brandState.items.find((item) => String(item.id) === String(chip.pendingUpgrade));
    if (brand) selectBrand(brand.id);
    setBrandFlow(false);
    router.push("/account/billing");
    return;
  }
  if (chip.pendingScan) { message.chips = null; message.action = null; startScan(chip.pendingScan.brand, chip.pendingScan.url, chip.pendingScan.cat, chip.pendingScan.comp, chip.pendingScan.sell, chip.pendingScan.buyer, false, chip.pendingScan.region || "", chip.pendingScan.city || ""); return; }
  if (chip.pendingDrop) { lsRemove("soa_pending_scan"); message.chips = null; message.action = null; return; }
  if (chip.view) { router.push({ path: `/${chip.view}` }); return; }
  if (chip.url) { window.open(chip.url, "_blank", "noopener"); return; }
  if (chip.build != null) {
    const b = String(chip.build || action?.brand || currentBrand.value?.brand || "").trim();
    openBuildPrep(b);
    return;
  }
  if (chip.studio) { if (chip.brand_id != null) openSiteWorkbench(chip.brand_id, event); else router.push("/studio"); return; }
  if (chip.deliverable) { openWorkbenchFromPayload({ type: "deliverable", slug: chip.deliverable.slug, title: chip.deliverable.title || "" }, event); return; }
  if (chip.task_id) { openWorkbenchFromPayload({ type: "task", id: chip.task_id }, event); return; }
  if (chip.planBuild != null) { openBuildPrep(chip.planBuild); return; }
  if (chip.planReport != null) { openWorkbenchFromPayload({ type: "report", brand: chip.planReport, sec: "overview" }, event); return; }
  if (chip.outreach) { void draftMessageOutreach(message); return; }
  if (chip.entity) {
    message.chips = null;
    askSentence(`监测 ${chip.entity}`, true);
    return;
  }
  if (chip.say) { askSentence(chip.say, false); return; }
  if (chip.drawer) { openWorkbenchFromPayload(chip.drawer, event); return; }
  if (chip.prev) { void choosePreviousSite(chip.prev); return; }
  runCardAction(chip, event);
}

onMounted(() => {
  document.addEventListener("keydown", handleWorkbenchKeys, true);
  window.addEventListener("soa:add-brand", startAddBrandConversation);
  window.addEventListener("soa:start-over-brand", startOverBrandConversation);
  unsubscribePlanExecution = onPlanExecutionCompleted(() => {
    void refreshMayaHistory().catch(() => {});
  });
  load();
});
onBeforeUnmount(() => {
  polling.stop?.();
  article.stopPoll?.();
  unsubscribePlanExecution?.();
  document.removeEventListener("keydown", handleWorkbenchKeys, true);
  window.removeEventListener("soa:add-brand", startAddBrandConversation);
  window.removeEventListener("soa:start-over-brand", startOverBrandConversation);
  if (wsHandle) { wsHandle.destroy(); wsHandle = null; }
  shell.wbTwoCol = false;
});
watch(() => route.query.intent, () => { if (route.query.intent === "add-brand" && !loading.value) startAddBrandConversation(); });
// deliverable links inside replies (data-mdeliv) open the deliverables page
onMounted(() => {
  document.getElementById("v-maya")?.addEventListener("click", (event) => {
    const btn = event.target.closest?.("[data-mdeliv]");
    if (!btn) return;
    event.preventDefault();
    router.push("/files");
  });
});
</script>

<style scoped>
.maya-vue .mmsg.maya { width: 100%; }
.maya-vue .mmsg.maya > .mcol { width: calc(100% - var(--mav) - var(--mavgap)); flex: 1 1 auto; }
.maya-vue .mmsg .mcol > .mc-cardwrap { width: 100%; margin-left: 0; box-sizing: border-box; }
.maya-noreport { max-width: 560px; margin: 32px auto; text-align: center; display: grid; gap: 18px; justify-items: center; }
.mc-phead img { width: 30px; height: 30px; border-radius: 50%; }
.mc-skillgroup + .mc-skillgroup { margin-top: 18px; }
.mc-pgroup { margin: 0 0 8px; color: var(--mute); font-size: var(--fs-eyebrow); font-weight: 700; letter-spacing: 0; text-transform: uppercase; }
.mc-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.mc-skill { min-height: 60px; border: 1px solid var(--line); border-radius: 14px; background: var(--card); padding: 10px 12px; text-align: left; display: grid; grid-template-columns: 34px minmax(0, 1fr); column-gap: 12px; align-items: center; cursor: pointer; color: var(--ink); }
.mc-skill:hover { border-color: var(--indigo); background: var(--tint-weak); }
.mc-skill-icon { display: inline-flex; width: 32px; height: 32px; align-items: center; justify-content: center; font-size: 26px; line-height: 1; }
.mc-skill-copy { min-width: 0; display: grid; gap: 3px; }
.mc-skill-copy b { font-size: var(--fs-subhead); }
/* F-402 · an Autopilot-only skill reads as locked BEFORE the click, not after the work. */
.mc-skill-locked { border-style: dashed; }
.mc-skill-locked .mc-skill-icon { opacity: .55; }
.mc-skill-lock { margin-left: 6px; padding: 1px 7px; border: 1px solid var(--tint-line); border-radius: 999px; background: var(--tint-weak); color: var(--indigo); font-size: var(--fs-caption2); font-weight: 650; vertical-align: middle; }
.mc-skill-copy small { color: var(--mute); line-height: 1.35; }
.mc-close { margin-top: 14px; width: 100%; border: 1px solid var(--line); border-radius: 12px; background: var(--card); color: var(--ink); min-height: 40px; cursor: pointer; }
.wb-simple { display: grid; gap: 12px; padding: 16px; }
.wb-json { white-space: pre-wrap; padding: 16px; color: var(--body); }
.mc-skill:focus-visible, .mc-close:focus-visible { outline: 3px solid var(--indigo); outline-offset: 2px; }
:deep(.art-gen:focus-visible), :deep(.art-regen:focus-visible), :deep(.art-publish:focus-visible), :deep(.art-copy:focus-visible), :deep(.art-wb:focus-visible), :deep(.art-plan:focus-visible), :deep(.au-ask:focus-visible), :deep(.au-force:focus-visible) { outline: 3px solid var(--indigo); outline-offset: 2px; }
.wb-deliv { padding: 16px; }
.wb-deliv .art-body { max-height: none; }
.mbub :deep(.mdraw) { white-space: pre-wrap; }
.mmsg-attachments { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.mmsg-attachment { max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding: 3px 8px; border: 1px solid var(--line); border-radius: 5px; background: var(--paper); color: var(--body); font-size: var(--fs-caption); }
.mmsg.user .mmsg-attachment { border-color: rgba(255, 255, 255, .45); background: rgba(255, 255, 255, .14); color: #fff; }
.mprofile-update { display: grid; grid-template-columns: 28px minmax(0, 1fr); gap: 10px; width: min(360px, 100%); box-sizing: border-box; padding: 11px 12px; border: 1px solid var(--line); border-radius: 8px; background: var(--card); }
.mprofile-update-mark { width: 28px; height: 28px; border-radius: 50%; background: rgba(14, 138, 109, .14); position: relative; }
.mprofile-update-mark::after { content: ""; position: absolute; left: 9px; top: 7px; width: 8px; height: 4px; border-left: 2px solid var(--jade); border-bottom: 2px solid var(--jade); transform: rotate(-45deg); }
.mprofile-update-copy { min-width: 0; }
.mprofile-update-copy p { margin: 0; color: var(--mute); font-size: var(--fs-caption); font-weight: 700; }
.mprofile-update-copy strong { display: block; margin: 2px 0 8px; color: var(--ink); font-size: var(--fs-subhead); line-height: 1.3; overflow-wrap: anywhere; }
.mprofile-update-copy ul { display: flex; flex-wrap: wrap; gap: 5px; margin: 0; padding: 0; list-style: none; }
.mprofile-update-copy li { padding: 2px 7px; border-radius: 4px; background: var(--tint-weak); color: var(--body); font-size: var(--fs-caption); line-height: 1.35; }
.mreason { margin: 0 0 10px; border-left: 3px solid var(--indigo); color: var(--mute); }
.mreason summary { cursor: pointer; padding: 2px 0 2px 9px; font-size: var(--fs-caption); font-weight: 700; }
.mreason-body { padding: 5px 9px 3px; color: var(--body); font-size: var(--fs-caption); line-height: 1.5; }
.mreason-body p { margin: 0 0 6px; }
.mreason summary:focus-visible { outline: 3px solid var(--indigo); outline-offset: 2px; }
.machips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
@media (max-width: 680px) { .mc-grid { grid-template-columns: 1fr; } }
@media (max-width: 560px) {
  #v-maya { --mlane: 8px; --mav: 28px; --mavgap: 8px; --mgut: calc(var(--mav) + var(--mavgap)); }
  :deep(.bp-card) { width: 100%; max-width: none; padding: 16px; box-sizing: border-box; }
  :deep(.bp-acts) { flex-direction: column; }
  :deep(.bp-go), :deep(.bp-skip) { width: 100%; }
}
@media (prefers-reduced-motion: reduce) { .mc-drawer.on, .mc-palette { animation: none; } }
</style>
